/**
 * SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/tenant-admin/TenantSettings.tsx
 * PURPOSE: Full 9-tab tenant settings page — general, branding, org tree,
 *          attendance config, schedule config, unit types (SC-008), late policy,
 *          checklist templates, shift types
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-16
 * COMPLIANCE: SDLC 6.5.0 + Sprint 77L + Zero Mock Tolerance
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance | Rule 7: Performance SLAs
 *
 * @business-context
 * - Domain: Tenant Administration, Organization Configuration
 * - Impact: CRITICAL — all tenant-level settings in one page
 * - Stakeholders: Tenant owners, tenant admins
 *
 * @technical-architecture
 * - Layer: Presentation Layer (Page Component)
 * - Pattern: Tab-based settings, 2s debounce auto-save, inline styles
 * - Dependencies: React 18, apiClient, useAuthStore, react-router-dom
 *
 * @cross-references
 * - API: backend/app/api/v2/tenant_admin/settings.py
 * - API: backend/app/api/v2/tenant_admin/checklist_crud.py
 * - API: backend/app/api/v2/tenant_admin/attendance_config.py
 * - API: backend/app/api/v2/tenant.py (GET/PUT /tenant/config — workforce_settings JSONB)
 * - Design: docs/02-design/04-ui-ux-design/02-tenant-management/TENANT-ADMIN-DASHBOARD-DESIGN.md
 * - Design: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md
 *   § SC-004-ATTENDANCE-CONFIG khối [7]/[8]/[9] (2026-08-17, BS-400 CFG-20..24)
 *   § SC-004-ATTENDANCE-CONFIG khối [4] (2026-08-18) — gỡ radio "Quên/thiếu
 *   chấm công" (miss_policy/makeup_window_hours) khỏi UI: 0 backend consumer,
 *   hành vi "quên chấm" thật do BS-400 §3.5/§3.6 (khối [7] CFG-22/23) cố định.
 *   Khối [8] (2026-08-18, BS-400 §7.2 Tầng 1 amend): ĐỔI từ thuần mô tả sang
 *   4 checkbox tương tác (`auto_record_*`) — Tenant Admin đặt baseline
 *   tenant-wide cho CFG-24. Khối [9] (Tầng 2, per-cơ-sở permission-grant) —
 *   DD-0105 (2026-08-25), bảng `branch_workforce_config_overrides` + API
 *   GET/PUT /tenant/branch-workforce-overrides — nay ĐÃ build (`BranchOverridesCard`).
 * - Model: backend/app/models/tenant_workforce_config.py (get_shift_match_thresholds())
 * - Business spec: docs/01-planning/02-uat-len-lich-cham-cong/
 *   BS-400-HE-THONG-HIEU-CA-KHOP-CA-PROPOSAL-2026-08-15.md §2/§7.2
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Navigate } from "react-router-dom";
import { useAuthStore } from "../../store/auth";
import { apiClient } from "../../services/api/client";
import { API_BASE_URL } from "../../services/api/config";
import { useDebounce } from "../../hooks/useDebounce";
import { PortalTooltip } from "../../components/common/PortalTooltip";
import { TimeTextInput } from "../../components/ui/TimeTextInput";
import UnitTypesTab from "./UnitTypesTab";
import BranchGeofenceTab from "./BranchGeofenceTab";
import PublicHolidaysTab from "./PublicHolidaysTab";

// =============================================================================
// Helpers
// =============================================================================

/** Resolve API base URL from the canonical single source (services/api/config.ts). */
const getApiBase = (version: string) => `${API_BASE_URL}/api/${version}`;

/** Format a Date as HH:mm in vi-VN locale */
const formatHHmm = (d: Date): string =>
  d.toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit", hour12: false });

// =============================================================================
// Types
// =============================================================================

interface TenantSettingsData {
  timezone: string;
  language: string;
  currency: string;
  date_format: string;
  ot_daily_cap_hours: number;
  ot_monthly_cap_hours: number;
  late_t0_max_minutes: number;
  late_t1_max_minutes: number;
  late_t2_max_minutes: number;
  late_t1_penalty_vnd: number;
  late_t2_penalty_vnd: number;
  late_t3_penalty_vnd: number;
  branding: Record<string, any>;
  custom_config: Record<string, any>;
}

interface OrgNode {
  id: string;
  name: string;
  name_vietnamese?: string;
  type?: string;
  org_type?: string;
  code?: string;
  address?: string;
  level?: number;
  children: OrgNode[];
}

interface ChecklistItem {
  id?: string;
  name: string;
  order: number;
  is_mandatory: boolean;
  requires_photo: boolean;
  requires_note: boolean;
}

interface ChecklistTemplate {
  id: string;
  name: string;
  department: string | null;
  shift_type: string;
  is_active: boolean;
  items: Array<{
    id: string;
    item_order: number;
    title_vi: string;
    is_mandatory: boolean;
    requires_photo: boolean;
    requires_note: boolean;
  }>;
}

// =============================================================================
// Styles
// =============================================================================

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f3f4f6",
  } as React.CSSProperties,
  nav: {
    background: "white",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    padding: "0 24px",
  } as React.CSSProperties,
  navInner: {
    // 2026-08-25 (UI-UX-DECISION-REGISTER.md luật 9, rollout — đóng nốt lệch
    // ISS-PILOT-25 để lại: nav từng cap cứng 1200px trong khi `main` bên dưới
    // đã nới lên min(90%,1440px), 2 vùng lệch bề rộng nhau cùng 1 màn):
    // bỏ maxWidth + margin:auto, khớp `main`.
    display: "flex",
    alignItems: "center",
    height: "64px",
    gap: "12px",
  } as React.CSSProperties,
  backBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "18px",
    color: "var(--nqh-coffee-500, #d97706)",
  } as React.CSSProperties,
  title: {
    fontSize: "18px",
    fontWeight: "bold" as const,
    color: "#1f2937",
    margin: 0,
  } as React.CSSProperties,
  saveIndicator: {
    marginLeft: "auto",
    fontSize: "13px",
    color: "#6b7280",
  } as React.CSSProperties,
  saveNowBtn: {
    marginLeft: "8px",
    padding: "4px 12px",
    fontSize: "13px",
    fontWeight: "500" as const,
    color: "var(--nqh-coffee-500, #d97706)",
    background: "var(--nqh-coffee-50, #fdf6ef)",
    border: "1px solid var(--nqh-coffee-200, #f0dcc3)",
    borderRadius: "6px",
    cursor: "pointer",
  } as React.CSSProperties,
  main: {
    // ISS-PILOT-25 (interim, superseded 2026-08-25): was maxWidth:"1200px",
    // then min(90%,1440px) — still a ceiling, still left 120px dead margin/side
    // at 1920px. UI-UX-DECISION-REGISTER.md luật 9 rollout: no cap at all —
    // `main` fills the full width available after the sidebar, matching
    // `navInner` above (was capped separately at 1200px, now consistent).
    padding: "24px",
  } as React.CSSProperties,
  tabBar: {
    display: "flex",
    gap: "0",
    borderBottom: "2px solid #e5e7eb",
    marginBottom: "24px",
    overflowX: "auto" as const,
    // ISS-PILOT-34: overflow-x alone lets the browser force an implicit
    // overflow-y:auto (CSS Overflow spec) — locks it to "hidden" to stop
    // the spurious vertical scrollbar sliver, without affecting horizontal scroll.
    overflowY: "hidden" as const,
  } as React.CSSProperties,
  tab: (active: boolean): React.CSSProperties => ({
    padding: "12px 20px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: active ? "600" : "400",
    color: active ? "var(--nqh-coffee-500, #d97706)" : "#6b7280",
    borderBottom: active ? "2px solid var(--nqh-coffee-500, #d97706)" : "2px solid transparent",
    marginBottom: "-2px",
    background: "none",
    border: "none",
    whiteSpace: "nowrap",
  }),
  card: {
    background: "white",
    borderRadius: "12px",
    padding: "24px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  } as React.CSSProperties,
  fieldGroup: {
    marginBottom: "20px",
  } as React.CSSProperties,
  label: {
    display: "block",
    fontSize: "14px",
    fontWeight: "500" as const,
    color: "#374151",
    marginBottom: "6px",
  } as React.CSSProperties,
  input: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "14px",
    boxSizing: "border-box" as const,
  } as React.CSSProperties,
  select: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "14px",
    boxSizing: "border-box" as const,
    background: "white",
  } as React.CSSProperties,
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
  } as React.CSSProperties,
  btn: (variant: "primary" | "danger" | "secondary"): React.CSSProperties => ({
    padding: "8px 16px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    border: "none",
    color: variant === "secondary" ? "#374151" : "white",
    background:
      variant === "primary"
        ? "var(--nqh-coffee-500, #d97706)"
        : variant === "danger"
        ? "#ef4444"
        : "#e5e7eb",
  }),
  toggle: (on: boolean): React.CSSProperties => ({
    width: "44px",
    height: "24px",
    borderRadius: "12px",
    background: on ? "var(--nqh-coffee-500, #d97706)" : "#d1d5db",
    cursor: "pointer",
    position: "relative",
    border: "none",
    transition: "background 0.2s",
  }),
  toggleCircle: (on: boolean): React.CSSProperties => ({
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    background: "white",
    position: "absolute",
    top: "2px",
    left: on ? "22px" : "2px",
    transition: "left 0.2s",
  }),
  treeNode: (level: number): React.CSSProperties => ({
    padding: "8px 12px",
    paddingLeft: `${12 + level * 24}px`,
    borderBottom: "1px solid #f3f4f6",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "14px",
  }),
  chip: (color: string): React.CSSProperties => ({
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: "9999px",
    fontSize: "12px",
    fontWeight: "500",
    background: color === "green" ? "#dcfce7" : color === "blue" ? "#dbeafe" : "#f3f4f6",
    color: color === "green" ? "#166534" : color === "blue" ? "#1e40af" : "#374151",
  }),
};

// =============================================================================
// Tab Components
// =============================================================================

const GeneralTab: React.FC<{
  settings: TenantSettingsData;
  onChange: (key: string, value: any) => void;
}> = ({ settings, onChange }) => (
  <div style={styles.card}>
    <h3 style={{ margin: "0 0 20px 0", fontSize: "16px", color: "#1f2937" }}>
      Thông tin chung
    </h3>
    <div style={styles.row}>
      <div style={styles.fieldGroup}>
        <label style={styles.label}>Múi giờ</label>
        <select
          style={styles.select}
          value={settings.timezone}
          onChange={(e) => onChange("timezone", e.target.value)}
        >
          <option value="Asia/Ho_Chi_Minh">Asia/Ho_Chi_Minh (GMT+7)</option>
          <option value="Asia/Bangkok">Asia/Bangkok (GMT+7)</option>
          <option value="UTC">UTC (GMT+0)</option>
        </select>
      </div>
      <div style={styles.fieldGroup}>
        <label style={styles.label}>Ngôn ngữ</label>
        <select
          style={styles.select}
          value={settings.language}
          onChange={(e) => onChange("language", e.target.value)}
        >
          <option value="vi">Tiếng Việt</option>
          <option value="en">English</option>
        </select>
      </div>
    </div>
    <div style={styles.row}>
      <div style={styles.fieldGroup}>
        <label style={styles.label}>Đơn vị tiền tệ</label>
        <select
          style={styles.select}
          value={settings.currency}
          onChange={(e) => onChange("currency", e.target.value)}
        >
          <option value="VND">VND</option>
          <option value="USD">USD</option>
        </select>
      </div>
      <div style={styles.fieldGroup}>
        <label style={styles.label}>Định dạng ngày</label>
        <select
          style={styles.select}
          value={settings.date_format}
          onChange={(e) => onChange("date_format", e.target.value)}
        >
          <option value="DD/MM/YYYY">DD/MM/YYYY</option>
          <option value="MM/DD/YYYY">MM/DD/YYYY</option>
          <option value="YYYY-MM-DD">YYYY-MM-DD</option>
        </select>
      </div>
    </div>
  </div>
);

const BrandingTab: React.FC<{
  settings: TenantSettingsData;
  onChange: (key: string, value: any) => void;
}> = ({ settings, onChange }) => {
  const branding = settings.branding || {};

  const handleBrandingChange = (field: string, value: string) => {
    onChange("branding", { ...branding, [field]: value });
  };

  return (
    <div style={styles.card}>
      <h3 style={{ margin: "0 0 20px 0", fontSize: "16px", color: "#1f2937" }}>
        Thương hiệu
      </h3>
      <div style={styles.fieldGroup}>
        <label style={styles.label}>Logo</label>
        <input
          type="file"
          accept="image/*"
          style={styles.input}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              const reader = new FileReader();
              reader.onloadend = () => {
                handleBrandingChange("logo_data_url", reader.result as string);
              };
              reader.readAsDataURL(file);
            }
          }}
        />
        {branding.logo_data_url && (
          <img
            src={branding.logo_data_url}
            alt="Logo"
            style={{ marginTop: "12px", maxHeight: "80px", borderRadius: "8px" }}
          />
        )}
      </div>
      <div style={styles.row}>
        <div style={styles.fieldGroup}>
          <label style={styles.label}>Mau chu dao</label>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <input
              type="color"
              value={branding.primary_color || "#4f46e5"}
              onChange={(e) => handleBrandingChange("primary_color", e.target.value)}
              style={{ width: "48px", height: "36px", border: "1px solid #d1d5db", borderRadius: "6px", cursor: "pointer" }}
            />
            <span style={{ fontSize: "13px", color: "#6b7280" }}>
              {branding.primary_color || "#4f46e5"}
            </span>
          </div>
        </div>
        <div style={styles.fieldGroup}>
          <label style={styles.label}>Mau phu</label>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <input
              type="color"
              value={branding.accent_color || "#10b981"}
              onChange={(e) => handleBrandingChange("accent_color", e.target.value)}
              style={{ width: "48px", height: "36px", border: "1px solid #d1d5db", borderRadius: "6px", cursor: "pointer" }}
            />
            <span style={{ fontSize: "13px", color: "#6b7280" }}>
              {branding.accent_color || "#10b981"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const OrgTreeTab: React.FC = () => {
  const [tree, setTree] = useState<OrgNode[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nodeCount, setNodeCount] = useState(0);

  // SPEC-0413 FR-002: Create/Edit modal state
  const [showModal, setShowModal] = useState(false);
  const [editingNode, setEditingNode] = useState<OrgNode | null>(null);
  const [parentNode, setParentNode] = useState<OrgNode | null>(null);
  const [modalForm, setModalForm] = useState({ name: "", code: "" });
  const [modalSaving, setModalSaving] = useState(false);
  const [modalError, setModalError] = useState("");

  const countNodes = (nodes: OrgNode[]): number => {
    let count = nodes.length;
    for (const n of nodes) {
      if (n.children) count += countNodes(n.children);
    }
    return count;
  };

  useEffect(() => {
    // Fetch from Sprint 77 org tree endpoint (v1), fallback to v2 tenant-admin
    const fetchTree = async () => {
      try {
        // Try Sprint 77 v1 endpoint first: GET /api/v1/organizations/{orgId}/tree
        const orgRes = await apiClient.get("/organizations", {
          baseURL: getApiBase("v1"),
        });
        const orgs = Array.isArray(orgRes.data) ? orgRes.data : orgRes.data?.items || [];
        if (orgs.length > 0) {
          const rootOrg = orgs[0];
          const treeRes = await apiClient.get(`/organizations/${rootOrg.id}/tree`, {
            baseURL: getApiBase("v1"),
          });
          const treeData = Array.isArray(treeRes.data) ? treeRes.data : [treeRes.data];
          setTree(treeData);
          setNodeCount(countNodes(treeData));
          return;
        }
      } catch {
        // Fallback to v2 tenant-admin endpoint
      }
      try {
        const res = await apiClient.get("/tenant-admin/organizations/tree", {
          baseURL: getApiBase("v2"),
        });
        const treeData = Array.isArray(res.data) ? res.data : [res.data];
        setTree(treeData);
        setNodeCount(countNodes(treeData));
      } catch {
        setError("Không thể tải cơ cấu tổ chức. Vui lòng thử lại sau.");
        setTree([]);
      }
    };
    fetchTree().finally(() => setLoading(false));
  }, []);

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    const allIds = new Set<string>();
    const collect = (nodes: OrgNode[]) => {
      for (const n of nodes) {
        if (n.children && n.children.length > 0) {
          allIds.add(n.id);
          collect(n.children);
        }
      }
    };
    collect(tree);
    setExpanded(allIds);
  };

  const collapseAll = () => {
    setExpanded(new Set());
  };

  // SPEC-0413 FR-002: Handlers for add/edit org nodes
  const handleAddChild = (parent: OrgNode) => {
    setEditingNode(null);
    setParentNode(parent);
    setModalForm({ name: "", code: "" });
    setModalError("");
    setShowModal(true);
  };

  const handleEditNode = (node: OrgNode) => {
    setEditingNode(node);
    setParentNode(null);
    setModalForm({ name: node.name || "", code: node.code || "" });
    setModalError("");
    setShowModal(true);
  };

  const handleAddRoot = () => {
    setEditingNode(null);
    setParentNode(null);
    setModalForm({ name: "", code: "" });
    setModalError("");
    setShowModal(true);
  };

  const handleModalSubmit = async () => {
    if (!modalForm.name.trim() || !modalForm.code.trim()) {
      setModalError("Tên và Mã là bắt buộc");
      return;
    }
    setModalSaving(true);
    setModalError("");
    try {
      if (editingNode) {
        await apiClient.patch(`/organizations/${editingNode.id}`, {
          name: modalForm.name,
          code: modalForm.code,
        }, { baseURL: getApiBase("v1") });
      } else {
        await apiClient.post("/organizations/", {
          name: modalForm.name,
          code: modalForm.code,
          parent_id: parentNode?.id || null,
        }, { baseURL: getApiBase("v1") });
      }
      setShowModal(false);
      // Refetch tree
      setLoading(true);
      const res = await apiClient.get("/tenant-admin/organizations/tree", { baseURL: getApiBase("v2") });
      const treeData = Array.isArray(res.data) ? res.data : [res.data];
      setTree(treeData);
      setNodeCount(countNodes(treeData));
      setLoading(false);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : Array.isArray(detail) ? detail.map((d: any) => d.msg ?? String(d)).join(", ")
        : "Thao tác thất bại";
      setModalError(msg);
    } finally {
      setModalSaving(false);
    }
  };

  const orgTypeLabels: Record<string, string> = {
    group: "Tập đoàn",
    chain: "Chuỗi",
    brand: "Thương hiệu",
    region: "Vùng",
    branch: "Chi nhánh",
    area: "Khu vực",
    department: "Phòng ban",
    team: "Nhóm",
  };

  const renderNode = (node: OrgNode, level: number): React.ReactNode => {
    const orgType = node.org_type || node.type || "";
    const displayName = node.name_vietnamese || node.name;
    const nodeLevel = node.level != null ? node.level : level;
    const typeColors: Record<string, string> = {
      group: "#7c3aed",
      chain: "#2563eb",
      brand: "#0891b2",
      region: "#059669",
      branch: "#d97706",
      area: "#dc2626",
      department: "#7c3aed",
      team: "#6366f1",
    };
    const chipColor = typeColors[orgType.toLowerCase()] || "#6b7280";
    const orgTypeDisplay = orgTypeLabels[orgType.toLowerCase()] || orgType;

    return (
      <React.Fragment key={node.id}>
        <div
          style={{
            ...styles.treeNode(level),
            background: level === 0 ? "#f9fafb" : "transparent",
          }}
        >
          {node.children && node.children.length > 0 ? (
            <button
              onClick={() => toggleExpand(node.id)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "14px",
                width: "20px",
                color: "#6b7280",
              }}
              title={expanded.has(node.id) ? "Thu gọn" : "Mở rộng"}
            >
              {expanded.has(node.id) ? "\u25BC" : "\u25B6"}
            </button>
          ) : (
            <span style={{ width: "20px", display: "inline-block", textAlign: "center", color: "#d1d5db" }}>
              &#x2022;
            </span>
          )}
          <span style={{ fontWeight: level === 0 ? "600" : "400" }}>
            {displayName}
          </span>
          {orgType && (
            <span
              style={{
                ...styles.chip(chipColor === "#6b7280" ? "gray" : "blue"),
                backgroundColor: `${chipColor}15`,
                color: chipColor,
                border: `1px solid ${chipColor}30`,
              }}
            >
              {orgTypeDisplay}
            </span>
          )}
          <span
            style={{
              fontSize: "11px",
              color: "#9ca3af",
              background: "#f3f4f6",
              padding: "1px 6px",
              borderRadius: "4px",
            }}
          >
            Cấp {nodeLevel}
          </span>
          {node.code && (
            <span style={{ fontSize: "12px", color: "#9ca3af", marginLeft: "4px" }}>
              [{node.code}]
            </span>
          )}
          {node.children && node.children.length > 0 && (
            <span style={{ fontSize: "11px", color: "#9ca3af" }}>
              ({node.children.length} con)
            </span>
          )}
          {node.address && (
            <span
              style={{
                fontSize: "12px",
                color: "#6b7280",
                marginLeft: "8px",
                fontStyle: "italic",
              }}
            >
              {node.address}
            </span>
          )}
          {/* SPEC-0413 FR-002: hover action buttons — Edit + Add Child */}
          <span style={{ marginLeft: "auto", display: "flex", gap: "4px" }}>
            <button
              onClick={(e) => { e.stopPropagation(); handleEditNode(node); }}
              style={{
                background: "none", border: "1px solid #d1d5db", borderRadius: "4px",
                cursor: "pointer", padding: "2px 6px", fontSize: "12px", color: "#6b7280",
              }}
              title="Sửa"
            >
              ✏️
            </button>
            {(nodeLevel ?? 0) < 8 && (
              <button
                onClick={(e) => { e.stopPropagation(); handleAddChild(node); }}
                style={{
                  background: "none", border: "1px solid #d1d5db", borderRadius: "4px",
                  cursor: "pointer", padding: "2px 6px", fontSize: "12px", color: "#6b7280",
                }}
                title="Thêm con"
              >
                ➕
              </button>
            )}
          </span>
        </div>
        {expanded.has(node.id) &&
          node.children?.map((child) => renderNode(child, level + 1))}
      </React.Fragment>
    );
  };

  if (loading) {
    return (
      <div style={styles.card}>
        <p style={{ color: "#6b7280" }}>Đang tải cơ cấu tổ chức...</p>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <div>
          <h3 style={{ margin: "0 0 4px 0", fontSize: "16px", color: "#1f2937" }}>
            Cơ cấu tổ chức
          </h3>
          {nodeCount > 0 && (
            <span style={{ fontSize: "12px", color: "#9ca3af" }}>
              Tổng cộng {nodeCount} đơn vị
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button style={styles.btn("primary")} onClick={handleAddRoot}>
            + Thêm tổ chức
          </button>
          {tree.length > 0 && (
            <>
              <button style={styles.btn("secondary")} onClick={expandAll}>
                Mở rộng tất cả
              </button>
              <button style={styles.btn("secondary")} onClick={collapseAll}>
                Thu gọn tất cả
              </button>
            </>
          )}
        </div>
      </div>
      {error && (
        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "16px",
            fontSize: "13px",
            color: "#991b1b",
          }}
        >
          {error}
        </div>
      )}
      {tree.length === 0 && !error ? (
        <p style={{ color: "#6b7280", fontSize: "14px" }}>
          Chưa có dữ liệu cơ cấu tổ chức.
        </p>
      ) : (
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          {tree.map((node) => renderNode(node, 0))}
        </div>
      )}

      {/* SPEC-0413 FR-002: Inline modal for Create/Edit organization */}
      {showModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            background: "#fff", borderRadius: "12px", padding: "24px",
            width: "100%", maxWidth: "440px", boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: "16px", color: "#1f2937" }}>
              {editingNode ? "Sửa tổ chức" : parentNode ? `Thêm con cho "${parentNode.name}"` : "Thêm tổ chức mới"}
            </h3>
            {modalError && (
              <div style={{
                background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px",
                padding: "10px", marginBottom: "12px", color: "#dc2626", fontSize: "13px",
              }}>
                {modalError}
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <div>
                <label style={styles.label}>Tên tổ chức *</label>
                <input
                  style={styles.input}
                  value={modalForm.name}
                  onChange={(e) => setModalForm({ ...modalForm, name: e.target.value })}
                  placeholder="VD: Buôn Kơ Lang"
                  autoFocus
                />
              </div>
              <div>
                <label style={styles.label}>Mã tổ chức *</label>
                <input
                  style={styles.input}
                  value={modalForm.code}
                  onChange={(e) => setModalForm({ ...modalForm, code: e.target.value.toUpperCase() })}
                  placeholder="VD: BKL"
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "20px" }}>
              <button style={styles.btn("secondary")} onClick={() => setShowModal(false)}>Hủy</button>
              <button style={styles.btn("primary")} onClick={handleModalSubmit} disabled={modalSaving}>
                {modalSaving ? "Đang lưu..." : editingNode ? "Cập nhật" : "Tạo"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// SC-004/SC-005 (WP-8) — Cấu hình Chấm công / Lịch làm việc theo mockup
// settings-attendance.html + settings-schedule.html: explicit Save (nút [5] Hủy
// + [6] Lưu thay đổi disabled tới khi DIRTY), KHÔNG auto-save debounce.
// Persist: tenant_workforce_configs.workforce_settings (WP-6a đọc labor_rules
// .max_hours_week/.publish_ahead_days/.violation_handling/.min_rest_between_shifts_hours)
// qua GET/PUT /api/v2/tenant/config.
// =============================================================================

/**
 * Cảnh báo ràng buộc pháp lý ngoài khung khuyến nghị — BE trả trong
 * `legal_warnings` của GET/PUT /api/v2/tenant/config (thêm 2026-09-16,
 * @cto review PR #943 mục 3). Quyết định nghiệp vụ 2026-09-14 là CẢNH BÁO
 * chứ không chặn; nếu cảnh báo không hiển thị được thì nó thành "không chặn
 * và không báo" — tức là im lặng. Shape khớp
 * `backend/app/api/v2/tenant.py::LaborRuleLegalWarning`; FE KHÔNG tự map
 * field→nhãn/điều luật (BE gửi sẵn `label_vi`/`legal_reference`/`message_vi`
 * để tránh drift kiểu SCHEDULE_DEFAULTS từng lệch khỏi BR).
 */
export interface LegalWarning {
  field: string;
  label_vi: string;
  value: number;
  recommended_bound: number;
  bound_kind: "min" | "max";
  legal_reference: string;
  message_vi: string;
}

interface TenantConfigResponse {
  workforce_settings?: Record<string, any>;
  legal_warnings?: LegalWarning[];
}

/** GET /api/v2/tenant/config → nguyên response (workforce_settings + legal_warnings) */
const fetchTenantConfig = async (): Promise<TenantConfigResponse> => {
  const res = await apiClient.get("/tenant/config", { baseURL: getApiBase("v2") });
  return res.data || {};
};

/** GET /api/v2/tenant/config → workforce_settings JSONB ({} khi chưa có) */
const fetchWorkforceSettings = async (): Promise<Record<string, any>> => {
  const data = await fetchTenantConfig();
  return data.workforce_settings || {};
};

/**
 * PUT merge: đọc bản mới nhất → gộp 1 section → ghi lại toàn bộ workforce_settings.
 * Trả về response của PUT (trước đây vứt đi) — `legal_warnings` trong đó là
 * đường DUY NHẤT cảnh báo tới được người vừa bấm Lưu.
 */
const saveWorkforceSection = async (
  section: string,
  values: Record<string, any>
): Promise<TenantConfigResponse> => {
  const current = await fetchWorkforceSettings();
  const merged = {
    ...current,
    [section]: { ...(current[section] || {}), ...values },
  };
  const res = await apiClient.put(
    "/tenant/config",
    { workforce_settings: merged },
    { baseURL: getApiBase("v2") }
  );
  return res.data || {};
};

// Styles dùng chung 2 tab + dialog DIRTY — token var(--nqh-*) (mockup cfg-*)
const cfgStyles = {
  sectionTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "var(--nqh-gray-900, #1f2937)",
    margin: "0 0 12px 0",
  } as React.CSSProperties,
  card: {
    border: "1px solid var(--nqh-gray-200, #e5e7eb)",
    borderRadius: "12px",
    background: "var(--nqh-bg-surface, white)",
    padding: "16px",
    marginBottom: "16px",
    // ISS-PILOT-24: was maxWidth:"560px" — an absolute cap (measured
    // identical px at 1440px AND 1920px viewports), not a "not yet scaled"
    // gap. Removed to match the width:"100%" pattern already proven in
    // UnitTypesTab.tsx (tabs "Mã ca"/"Loại đơn vị", same page/session).
  } as React.CSSProperties,
  cardTitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "var(--nqh-gray-900, #1f2937)",
    marginBottom: "12px",
  } as React.CSSProperties,
  check: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    fontSize: "14px",
    color: "var(--nqh-gray-700, #374151)",
    padding: "4px 0",
    cursor: "pointer",
  } as React.CSSProperties,
  rowLine: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "14px",
    color: "var(--nqh-gray-700, #374151)",
    padding: "4px 0",
    flexWrap: "wrap" as const,
  } as React.CSSProperties,
  numInput: {
    width: "90px",
    padding: "6px 10px",
    border: "1px solid var(--nqh-gray-300, #d1d5db)",
    borderRadius: "8px",
    fontSize: "14px",
    boxSizing: "border-box" as const,
  } as React.CSSProperties,
  selectInput: {
    width: "140px",
    padding: "6px 10px",
    border: "1px solid var(--nqh-gray-300, #d1d5db)",
    borderRadius: "8px",
    fontSize: "14px",
    background: "white",
    boxSizing: "border-box" as const,
  } as React.CSSProperties,
  radios: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "4px",
    fontSize: "14px",
    color: "var(--nqh-gray-700, #374151)",
    marginTop: "4px",
  } as React.CSSProperties,
  radioLabel: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    cursor: "pointer",
    minHeight: "32px",
  } as React.CSSProperties,
  label: {
    fontSize: "14px",
    color: "var(--nqh-gray-700, #374151)",
    fontWeight: 600,
    marginTop: "8px",
  } as React.CSSProperties,
  fieldError: {
    marginTop: "6px",
    fontSize: "12px",
    color: "var(--nqh-chili, #dc2626)",
  } as React.CSSProperties,
  // BOT-041 (#810), 2026-09-14: quyết định nghiệp vụ đổi 6 field "Ràng buộc pháp
  // lý khi xếp ca" từ CHẶN sang CẢNH BÁO (BR-ATTN-004 §4.0 v2.2.0) — "không có
  // chặn, nếu luật thay đổi thì sao, chỉ warning thôi". Dùng màu coffee (cùng
  // token với InfoIcon warn=true) để phân biệt trực quan với fieldError (đỏ,
  // vẫn chặn lưu — vd otErr của BOT-039, publishErr).
  fieldWarning: {
    marginTop: "6px",
    fontSize: "12px",
    color: "var(--nqh-coffee-500, #d97706)",
  } as React.CSSProperties,
  actions: {
    display: "flex",
    // ISS-PILOT-24: was justifyContent:"space-between" — only looked
    // reasonable because the card was artificially capped at 560px wide, so
    // "space-between" put the 2 buttons a modest distance apart. With that
    // cap gone, space-between would pin Hủy to the far left edge of a
    // much wider card. flex-end matches the button-row pattern already used
    // everywhere else in this file (e.g. lines ~818, ~2695, ~2719, ~2739).
    justifyContent: "flex-end",
    gap: "12px",
  } as React.CSSProperties,
  btnPrimary: {
    padding: "8px 16px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    border: "none",
    color: "white",
    background: "var(--nqh-coffee-500, #d97706)",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
  } as React.CSSProperties,
  btnSecondary: {
    padding: "8px 16px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    border: "none",
    color: "var(--nqh-gray-700, #374151)",
    background: "var(--nqh-gray-200, #e5e7eb)",
  } as React.CSSProperties,
  spinner: {
    display: "inline-block",
    width: "14px",
    height: "14px",
    border: "2px solid rgba(255,255,255,0.4)",
    borderTopColor: "white",
    borderRadius: "50%",
    animation: "nqh-cfg-spin 0.8s linear infinite",
  } as React.CSSProperties,
  toast: {
    position: "fixed" as const,
    bottom: "24px",
    left: "50%",
    transform: "translateX(-50%)",
    background: "var(--nqh-gray-900, #1f2937)",
    color: "white",
    padding: "10px 20px",
    borderRadius: "8px",
    fontSize: "14px",
    zIndex: 1100,
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
  } as React.CSSProperties,
  overlay: {
    position: "fixed" as const,
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  } as React.CSSProperties,
  modal: {
    background: "white",
    borderRadius: "12px",
    padding: "24px",
    maxWidth: "420px",
    width: "90%",
  } as React.CSSProperties,
  modalTitle: {
    margin: "0 0 12px",
    fontSize: "18px",
    fontWeight: 600,
    color: "var(--nqh-gray-900, #1f2937)",
  } as React.CSSProperties,
  modalFoot: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    marginTop: "16px",
  } as React.CSSProperties,
};

const CFG_SPIN_STYLE = "@keyframes nqh-cfg-spin { to { transform: rotate(360deg); } }";

// Thay annotation-luôn-hiện (đoạn mô tả/cảnh báo dưới tiêu đề card, dưới
// checkbox) bằng icon ⓘ hover/focus tooltip — màn SC-004 bớt rối, nội dung
// vẫn tra được khi cần. Đúng pattern đã có sẵn trong codebase (PortalTooltip +
// InfoIcon, dùng ở ManagerAttendancePage.tsx cột bảng SC-011) thay vì tự chế
// `title` attribute — portal tránh bị card `overflow` cắt đứng, tự lật/kẹp mép
// viewport. `warn` tô màu coffee giống cảnh báo cũ; mặc định xám trung tính.
const InfoIcon: React.FC<{ warn?: boolean }> = ({ warn }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke={warn ? "var(--nqh-coffee-500, #d97706)" : "var(--nqh-gray-500, #9ca3af)"}
    strokeWidth={1.5}
    width={14}
    height={14}
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
    />
  </svg>
);

const InfoTip: React.FC<{ text: string; warn?: boolean }> = ({ text, warn }) => (
  <PortalTooltip text={text} ariaLabel={text} tabIndex={0}>
    <span style={{ display: "inline-flex", marginLeft: "6px", verticalAlign: "middle", cursor: "help" }}>
      <InfoIcon warn={warn} />
    </span>
  </PortalTooltip>
);

// ISS-PILOT-24 (2026-08-09): cfgStyles.card/actions lost their hard-coded
// maxWidth:"560px" (measured: 38.9% of 1440px / 29.2% of 1920px screen width,
// identical pixel value at both viewports — an absolute cap, not "not yet
// scaled").
//
// ISS-PILOT-24 REOPEN (2026-08-10, Hang Le): the first fix above wrapped each
// field group in `repeat(auto-fit, minmax(220px,1fr))`, which — once the
// 560px cap was gone and the card had ~1152px to work with — auto-flowed
// EVERY field in a group onto a single row (verified live @1920×1080:
// distinct_rows=1 for all 4 groups). Requirement: rows, not columns.
// Real field count per group (counted from JSX below, not assumed):
//   - Chấm công › "Cảnh báo & xử lý chấm công": 4 field  → target 4 hàng
//   - Lịch › "Chu kỳ & công bố lịch":            2 field  → target 2 hàng
//   - Lịch › "Ràng buộc pháp lý khi xếp ca":      3 field  → target 3 hàng
//   - Lịch › "Trần tăng ca" (con của trên):       3 field  → target 3 hàng
// Every group has ≤4 fields, so cột = ceil(field-count / 4) = 1 cột cho CẢ
// 4 nhóm hiện có → fixed single column (one field per row) makes each group
// render as exactly field-count rows, satisfying "4 hàng" for the 4-field
// group this requirement was written against. Renamed cfg-grid-2col →
// cfg-field-rows since it is no longer a multi-column grid.
const CFG_GRID_STYLE = `
.cfg-field-rows { display: flex; flex-direction: column; gap: 4px; }
`;

// =============================================================================
// SC-004-ATTENDANCE-CONFIG — tab "Chấm công"
// =============================================================================

interface AttendanceForm {
  method_selfie_photo: boolean;
  method_gps_location: boolean;
  /** MỚI 2026-08-29 (amend registry SC-004-ATTENDANCE-CONFIG khối [3]) —
   *  "Chấm công thủ công (Timemark)" toggle TENANT-WIDE, phương thức thứ 3
   *  cạnh selfie/GPS. Mặc định bật (khớp registry STATE DEFAULT "cả 3 mặc
   *  định bật"). Key JSONB `method_timemark` — KHÔNG trùng với field
   *  `gps_enabled` ở tenant_admin/attendance_config.py (per-branch
   *  verification-methods; tên `timemark_enabled` trước 2026-09-01, đổi vì
   *  bug đặt tên lịch sử — field đó thực ra ánh xạ tới phương thức GPS, xem
   *  get_branch_clock_config() comment mapping trong attendance.py) — chọn
   *  tên MỚI rõ ràng cho field NÀY để không nối tiếp bug. Xem cascade tương
   *  ứng: get_branch_clock_config() `method_timemark_enabled`. */
  method_timemark: boolean;
  warn_checkin_before_minutes: number;
  warn_missing_checkout_after_minutes: number;
  /** BS-004-R11 / BS-300 §1bis-G5 (chốt @cto 2026-08-05) — ngưỡng chấm sớm
   *  nhất trước ca, TA cấu hình được (KHÔNG hardcode phía mobile). */
  early_clockin_tolerance_minutes: number;
  /** ISS-PILOT-21 (2026-08-09) — 1 field này đã có consumer thật từ trước
   *  (manager_attendance.py:392-393, manager_staff.py:215 đọc đúng JSONB path
   *  workforce_settings.attendance.gps_radius_meters) nhưng KHÔNG có field
   *  nào trên form để TA đổi — luôn rơi về default cứng 50. Bổ sung field,
   *  không đổi backend (backend đã đúng). */
  gps_radius_meters: number;
  /** BS-400 §2 CFG-20 (2026-08-17, SC-011-ATTEND-MGR redesign) — "Dung sai
   *  check-in" ở khối [7] SC-004-ATTENDANCE-CONFIG. CÙNG khái niệm với field
   *  LEGACY `late_threshold_minutes` mà block [4] "Cảnh báo & xử lý chấm
   *  công" từng dùng nhãn "Tính là đi trễ: sau giờ vào ca" (ISS-PILOT-21) —
   *  SC-011-ATTENDANCE-API-CONTRACT-V1.0.md §1.2 xác nhận:
   *  "late_threshold_minutes (old field) is kept and now equals
   *  check_in_tolerance_minutes". Đây là 1 field DUY NHẤT trên form (không
   *  render 2 input cùng ý nghĩa "đi trễ vẫn tính đúng giờ") — chỉ đổi VỊ TRÍ
   *  (chuyển từ block [4] sang block [7] theo thiết kế đích) + NHÃN + key
   *  JSONB chính. `handleSave()` ghi CẢ 2 key (`check_in_tolerance_minutes`
   *  + `late_threshold_minutes`) vì `manager_staff.py:237` đọc thẳng
   *  `late_threshold_minutes` bằng `.get(...)`, KHÔNG qua
   *  `get_shift_match_thresholds()` nên không tự fallback — bỏ ghi key cũ sẽ
   *  làm call site đó rơi về giá trị cũ/mặc định một cách âm thầm. */
  check_in_tolerance_minutes: number;
  /** BS-400 §2 CFG-21 — "Dung sai check-out" (khối [7]). SUPERSEDED 2026-08-25
   *  (CHỐT C1+C2): 2 PHÍA — cùng số áp cho "Về sớm" (trước ca) VÀ ngưỡng
   *  "Quên chấm ra" (sau ca). CFG-23 (`missed_check_out_threshold_minutes`)
   *  đã BỎ hẳn — KHÔNG còn field riêng, xem BS-400 §2/§10-Q1 (đóng). */
  check_out_early_tolerance_minutes: number;
  /** BS-400 §2 CFG-22 — "Ngưỡng quên chấm vào" (khối [7]). Ràng buộc CHỐT
   *  2026-08-25: PHẢI > check_in_tolerance_minutes (validate ở handleSave). */
  missed_check_in_threshold_minutes: number;
  /** BS-400 §7.2 Tầng 1 CFG-24 (amend 2026-08-18, defaults superseded quyết
   *  định A 2026-08-20 + §10-Q8 đóng 2026-08-25) — 4 công tắc auto-record
   *  ĐỘC LẬP, baseline TENANT-WIDE cho khối [8] "Tự động ghi nhận công (theo
   *  cơ sở)". Áp cho mọi cơ sở CHƯA được cấp quyền tự cấu hình riêng ở khối
   *  [9] (đã build — bảng `branch_workforce_config_overrides`, DD-0105).
   *  Giá trị mặc định giờ TẤT CẢ false (☐☐☐☐, THỦ CÔNG toàn bộ) — khớp
   *  ShiftMatchThresholds + tenant_workforce_config.py DEFAULT_AUTO_RECORD_*.
   *  "Quên chấm ra"/"Chấm công không theo ca" đã WIRED tới backend enforcement
   *  thật; "Thiếu chấm công"/"Không khớp ca nào" chỉ LƯU giá trị — backend
   *  CHƯA enforce (công thức tự động ghi nhận vẫn là câu hỏi mở giao
   *  @architect, BS-400 §3.4bis — khác câu hỏi default, đã đóng ở Q8). */
  auto_record_missed_check_out: boolean;
  /** BS-400 §7.2 CFG-24 bucket "Chấm công không theo ca" (§3.3) — WIRED. */
  auto_record_off_schedule: boolean;
  /** BS-400 §7.2 CFG-24 bucket "Thiếu chấm công" (§3.6) — mặc định TẮT,
   *  CHƯA enforce (§3.4bis, xem ghi chú field phía trên). */
  auto_record_missing: boolean;
  /** BS-400 §7.2 CFG-24 bucket "Không khớp ca nào" (§3.2/§3.4bis/§3.7) —
   *  mặc định TẮT, CHƯA enforce (cùng lý do trên). */
  auto_record_unmatched: boolean;
}

// Mặc định theo mockup settings-attendance.html (cả 2 phương thức bật, 15/30 phút,
// chấm sớm nhất 30 phút — BS-004-R11)
const ATTENDANCE_DEFAULTS: AttendanceForm = {
  method_selfie_photo: true,
  method_gps_location: true,
  method_timemark: true,
  warn_checkin_before_minutes: 15,
  warn_missing_checkout_after_minutes: 30,
  early_clockin_tolerance_minutes: 30,
  // ISS-PILOT-21: khớp default hard-code hiện có ở backend
  // (manager_attendance.py .get("gps_radius_meters", 50)) — tenant chưa từng
  // lưu field này vẫn có hành vi giống hệt trước khi thêm field.
  gps_radius_meters: 50,
  // BS-400 §2 CFG-20/21/22 (2026-08-17, CFG-23 bỏ CHỐT C2 2026-08-25) — khớp
  // default đã ratify cho SC-004 khối [7] STATE `DEFAULT` (15/15/60) VÀ khớp
  // TenantWorkforceConfig model default + get_shift_match_thresholds()
  // DEFAULT_* constants (backend/app/models/tenant_workforce_config.py).
  check_in_tolerance_minutes: 15,
  check_out_early_tolerance_minutes: 15,
  missed_check_in_threshold_minutes: 60,
  // BS-400 §7.2 Tầng 1 CFG-24, quyết định A (2026-08-20) + §10-Q8 ĐÃ TRẢ LỜI
  // 2026-08-25 (@cto dvhiep xác nhận trực tiếp): mặc định khởi tạo TẤT CẢ 4
  // bucket = THỦ CÔNG (☐☐☐☐) — khớp ShiftMatchThresholds field defaults +
  // backend/app/models/tenant_workforce_config.py DEFAULT_AUTO_RECORD_*
  // constants (cả 4 đều False).
  auto_record_missed_check_out: false,
  auto_record_off_schedule: false,
  auto_record_missing: false,
  auto_record_unmatched: false,
};

// =============================================================================
// [9] Quyền tự cấu hình theo cơ sở — DD-0105 (2026-08-25).
// GET/PUT /api/v2/tenant/branch-workforce-overrides. Self-contained card:
// load/dirty/save riêng (xem ghi chú deviation tại nơi gọi component).
// =============================================================================

interface BranchOverrideRow {
  organization_id: string;
  branch_name: string;
  branch_code: string;
  allow_auto_record_override: boolean;
  allow_tolerance_override: boolean;
}

const BranchOverridesCard: React.FC = () => {
  const [rows, setRows] = useState<BranchOverrideRow[]>([]);
  const [snapshot, setSnapshot] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  // Tái dùng ĐÚNG hành vi tìm-1-chuỗi ở SC-011-ATTEND-MGR [1b]
  // (ManagerAttendancePage.tsx debouncedEmployeeSearch, 300ms) — registry yêu
  // cầu tái dùng, không tự đặt debounce riêng.
  const debouncedSearch = useDebounce(search, 300);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await apiClient.get("/tenant/branch-workforce-overrides", {
        baseURL: getApiBase("v2"),
      });
      const branches: BranchOverrideRow[] = res.data?.branches || [];
      setRows(branches);
      setSnapshot(JSON.stringify(branches));
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const dirty = snapshot !== null && JSON.stringify(rows) !== snapshot;

  const toggle = (organizationId: string, key: "allow_auto_record_override" | "allow_tolerance_override") => {
    setRows((prev) =>
      prev.map((r) => (r.organization_id === organizationId ? { ...r, [key]: !r[key] } : r))
    );
  };

  const handleCancel = () => {
    if (snapshot) setRows(JSON.parse(snapshot));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiClient.put(
        "/tenant/branch-workforce-overrides",
        {
          items: rows.map((r) => ({
            organization_id: r.organization_id,
            allow_auto_record_override: r.allow_auto_record_override,
            allow_tolerance_override: r.allow_tolerance_override,
          })),
        },
        { baseURL: getApiBase("v2") }
      );
      const branches: BranchOverrideRow[] = res.data?.branches || rows;
      setRows(branches);
      setSnapshot(JSON.stringify(branches));
      setToast("Đã lưu quyền tự cấu hình theo cơ sở");
    } catch {
      setToast("Lỗi lưu cấu hình — thử lại");
    } finally {
      setSaving(false);
    }
  };

  const visibleRows = debouncedSearch.trim()
    ? rows.filter((r) => r.branch_name.toLowerCase().includes(debouncedSearch.trim().toLowerCase()))
    : rows;

  return (
    <div style={cfgStyles.card}>
      <div style={cfgStyles.cardTitle}>
        Quyền tự cấu hình theo cơ sở
        <InfoTip text="Cho phép cơ sở tự đặt Tự ghi nhận và Dung sai riêng, khác mặc định chung." />
      </div>

      {loading ? (
        <p style={{ color: "var(--nqh-gray-500, #6b7280)" }}>Đang tải...</p>
      ) : loadError ? (
        <div>
          <p style={{ color: "var(--nqh-gray-500, #6b7280)" }}>Không tải được. Thử lại</p>
          <button style={cfgStyles.btnPrimary} onClick={load}>
            Thử lại
          </button>
        </div>
      ) : (
        <>
          <input
            type="text"
            placeholder="Tìm cơ sở…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ ...cfgStyles.numInput, width: "220px", marginBottom: "12px" }}
          />
          {visibleRows.length === 0 ? (
            <p style={{ color: "var(--nqh-gray-500, #6b7280)", fontSize: "14px" }}>
              {rows.length === 0 ? "Chưa có cơ sở nào." : "Không tìm thấy cơ sở phù hợp."}
            </p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--nqh-gray-200, #e5e7eb)" }}>
                  <th style={{ textAlign: "left", padding: "8px 4px" }}>Cơ sở</th>
                  <th style={{ textAlign: "left", padding: "8px 4px" }}>Tự ghi nhận</th>
                  <th style={{ textAlign: "left", padding: "8px 4px" }}>Dung sai</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r) => (
                  <tr key={r.organization_id} style={{ borderBottom: "1px solid var(--nqh-gray-100, #f3f4f6)" }}>
                    <td style={{ padding: "8px 4px" }}>{r.branch_name}</td>
                    <td style={{ padding: "8px 4px" }}>
                      <input
                        type="checkbox"
                        checked={r.allow_auto_record_override}
                        onChange={() => toggle(r.organization_id, "allow_auto_record_override")}
                      />
                    </td>
                    <td style={{ padding: "8px 4px" }}>
                      <input
                        type="checkbox"
                        checked={r.allow_tolerance_override}
                        onChange={() => toggle(r.organization_id, "allow_tolerance_override")}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ ...cfgStyles.actions, marginTop: "16px" }}>
            <button
              type="button"
              style={cfgStyles.btnSecondary}
              onClick={handleCancel}
              disabled={!dirty || saving}
            >
              Hủy
            </button>
            <button
              type="button"
              style={cfgStyles.btnPrimary}
              onClick={handleSave}
              disabled={!dirty || saving}
              aria-disabled={!dirty || saving}
            >
              {saving ? "Đang lưu..." : "Lưu quyền cơ sở"}
            </button>
          </div>
          {toast && (
            <div style={{ ...cfgStyles.fieldError, color: "var(--nqh-herb-600, #16a34a)", marginTop: "8px" }}>
              {toast}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const AttendanceConfigTab: React.FC<{
  onDirtyChange?: (dirty: boolean) => void;
}> = ({ onDirtyChange }) => {
  const [form, setForm] = useState<AttendanceForm>(ATTENDANCE_DEFAULTS);
  const [snapshot, setSnapshot] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [methodErr, setMethodErr] = useState(false);
  // [7] Dung sai xác định trạng thái — "4 ô số nguyên dương" (registry STATE VALIDATE)
  const [toleranceErr, setToleranceErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const dirty = snapshot !== null && JSON.stringify(form) !== snapshot;

  // Báo DIRTY lên parent → đổi tab hiện dialog xác nhận (mockup modalDirty)
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // Toast tự ẩn sau 4s (pattern window.nqhToast trong mockup common.js)
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const ws = await fetchWorkforceSettings();
      const a = ws.attendance || {};
      const loaded: AttendanceForm = {
        // ECO-DEC-2026-144 W2: GPS + selfie is always on (backend refuses False).
        method_selfie_photo: true,
        method_gps_location: a.method_gps_location ?? ATTENDANCE_DEFAULTS.method_gps_location,
        method_timemark: a.method_timemark ?? ATTENDANCE_DEFAULTS.method_timemark,
        warn_checkin_before_minutes:
          a.warn_checkin_before_minutes ?? ATTENDANCE_DEFAULTS.warn_checkin_before_minutes,
        warn_missing_checkout_after_minutes:
          a.warn_missing_checkout_after_minutes ??
          ATTENDANCE_DEFAULTS.warn_missing_checkout_after_minutes,
        early_clockin_tolerance_minutes:
          a.early_clockin_tolerance_minutes ??
          ATTENDANCE_DEFAULTS.early_clockin_tolerance_minutes,
        gps_radius_meters: a.gps_radius_meters ?? ATTENDANCE_DEFAULTS.gps_radius_meters,
        // Fallback chain mirrors TenantWorkforceConfig.get_shift_match_thresholds():
        // key mới tường minh → LEGACY `late_threshold_minutes` → default.
        check_in_tolerance_minutes:
          a.check_in_tolerance_minutes ??
          a.late_threshold_minutes ??
          ATTENDANCE_DEFAULTS.check_in_tolerance_minutes,
        check_out_early_tolerance_minutes:
          a.check_out_early_tolerance_minutes ??
          ATTENDANCE_DEFAULTS.check_out_early_tolerance_minutes,
        missed_check_in_threshold_minutes:
          a.missed_check_in_threshold_minutes ??
          ATTENDANCE_DEFAULTS.missed_check_in_threshold_minutes,
        // BS-400 §7.2 Tầng 1 CFG-24 — 4 key MỚI hoàn toàn (2026-08-18), không
        // có legacy alias nào cần fallback thêm (khác check_in_tolerance_minutes).
        auto_record_missed_check_out:
          a.auto_record_missed_check_out ?? ATTENDANCE_DEFAULTS.auto_record_missed_check_out,
        auto_record_off_schedule:
          a.auto_record_off_schedule ?? ATTENDANCE_DEFAULTS.auto_record_off_schedule,
        auto_record_missing:
          a.auto_record_missing ?? ATTENDANCE_DEFAULTS.auto_record_missing,
        auto_record_unmatched:
          a.auto_record_unmatched ?? ATTENDANCE_DEFAULTS.auto_record_unmatched,
      };
      setForm(loaded);
      setSnapshot(JSON.stringify(loaded));
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = <K extends keyof AttendanceForm>(key: K, value: AttendanceForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // [5] Hủy — khôi phục giá trị đã tải/lưu gần nhất
  const handleCancel = () => {
    if (snapshot) setForm(JSON.parse(snapshot));
    setMethodErr(false);
    setToleranceErr(null);
  };

  // [6] Lưu thay đổi — VALIDATE → SAVING → toast "Đã lưu cấu hình chấm công"
  const handleSave = async () => {
    const badMethod =
      !form.method_selfie_photo && !form.method_gps_location && !form.method_timemark;
    // [7] "3 ô số nguyên dương" — số âm/bỏ trống/0 đều không hợp lệ
    const nonPositive = [
      form.check_in_tolerance_minutes,
      form.check_out_early_tolerance_minutes,
      form.missed_check_in_threshold_minutes,
    ].some((v) => !Number.isFinite(v) || v <= 0);
    // CHỐT 2026-08-25 — validate tĩnh: CFG-22 (Ngưỡng quên chấm vào) phải LỚN
    // HƠN CFG-20 (Dung sai check-in), đóng BS-400 §10-Q1 (2 giá trị cùng
    // anchor "phút sau giờ vào ca" nên so trực tiếp được).
    const badOrder =
      !nonPositive &&
      form.missed_check_in_threshold_minutes <= form.check_in_tolerance_minutes;
    const toleranceMessage = nonPositive
      ? "Giá trị phải là số dương"
      : badOrder
        ? "Ngưỡng quên chấm vào phải lớn hơn dung sai check-in"
        : null;
    setMethodErr(badMethod);
    setToleranceErr(toleranceMessage);
    if (badMethod || toleranceMessage) return;

    setSaving(true);
    try {
      await saveWorkforceSection("attendance", {
        ...form,
        // LEGACY alias — SC-011-ATTENDANCE-API-CONTRACT-V1.0.md §1.2:
        // "late_threshold_minutes (old field) is kept and now equals
        // check_in_tolerance_minutes". manager_staff.py:237 reads this key
        // directly (not via get_shift_match_thresholds()'s fallback chain),
        // so it must be written explicitly here in lock-step with the new key.
        late_threshold_minutes: form.check_in_tolerance_minutes,
      });
      setSnapshot(JSON.stringify(form));
      setToast("Đã lưu cấu hình chấm công");
    } catch {
      setToast("Lỗi lưu cấu hình — thử lại");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.card}>
        <p style={{ color: "var(--nqh-gray-500, #6b7280)" }}>Đang tải cấu hình chấm công...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={styles.card}>
        <p style={{ color: "var(--nqh-gray-500, #6b7280)" }}>Không tải được cấu hình. Thử lại</p>
        <button style={cfgStyles.btnPrimary} onClick={load}>
          Thử lại
        </button>
      </div>
    );
  }

  return (
    <div>
      <style>{CFG_SPIN_STYLE}{CFG_GRID_STYLE}</style>
      <h3 style={cfgStyles.sectionTitle}>Cấu hình Chấm công</h3>

      {/* [3] Phương thức chấm công — chọn nhiều, bật ít nhất 1.
          ISS-PILOT-20 / DD-0092 §5 (2026-08-09): cascade tenant-wide → literal
          đã nối dây thật (trước đó 2 checkbox này không ai đọc). Cấu hình
          RIÊNG theo từng chi nhánh (per-branch override) đã có API sẵn
          (backend/app/api/v2/tenant_admin/attendance_config.py) nhưng CHƯA
          có UI — chặn bởi cùng gap dữ liệu đã nêu ở ISS-PILOT-6/7 (chi
          nhánh trong cây tổ chức hiện tại chưa liên kết với bảng branches
          cũ mà API đó dùng khoá). Xem DD-0092 §5. */}
      <div style={cfgStyles.card}>
        <div style={cfgStyles.cardTitle}>
          Phương thức chấm công
          <InfoTip text="Áp dụng cho toàn bộ chi nhánh." />
        </div>
        <label style={cfgStyles.check}>
          <input
            type="checkbox"
            checked
            disabled
            readOnly
          />
          <span>
            Chụp ảnh selfie (có GPS + timemark) — phương thức chuẩn, luôn bật cho mọi chi nhánh
          </span>
        </label>
        <label style={cfgStyles.check}>
          <input
            type="checkbox"
            checked={form.method_gps_location}
            onChange={(e) => update("method_gps_location", e.target.checked)}
          />
          <span>
            Thông tin định vị (GPS + thời gian) từ điện thoại — dùng khi máy ảnh nhân viên
            không sử dụng được
          </span>
        </label>
        <label style={cfgStyles.check}>
          <input
            type="checkbox"
            checked={form.method_timemark}
            onChange={(e) => update("method_timemark", e.target.checked)}
          />
          <span>
            Chấm công thủ công (Timemark) — dùng khi cả 2 cách trên đều không dùng được, cần
            Quản lý chi nhánh phê duyệt
          </span>
        </label>
        {methodErr && (
          <div style={cfgStyles.fieldError}>Cần bật ít nhất 1 phương thức chấm công</div>
        )}
        <div style={cfgStyles.rowLine}>
          <span>Bán kính GPS hợp lệ: trong</span>
          <input
            type="number"
            min={1}
            style={cfgStyles.numInput}
            value={form.gps_radius_meters}
            onChange={(e) => update("gps_radius_meters", Number(e.target.value))}
            disabled={!form.method_gps_location}
          />
          <span>mét quanh chi nhánh</span>
        </div>
      </div>

      {/* [4] Cảnh báo chấm công */}
      <div style={cfgStyles.card}>
        <div style={cfgStyles.cardTitle}>Cảnh báo chấm công</div>
        {/* ISS-PILOT-24 REOPEN: field ngắn (phút) → 1 cột = N hàng. Radio group
            "Quên/thiếu chấm công" từng ở đây đã GỠ 2026-08-18 (0 backend
            consumer — xem header @cross-references). */}
        <div className="cfg-field-rows">
          <div style={cfgStyles.rowLine}>
            <span>Cảnh báo vào ca: trước ca</span>
            <input
              type="number"
              min={0}
              style={cfgStyles.numInput}
              value={form.warn_checkin_before_minutes}
              onChange={(e) => update("warn_checkin_before_minutes", Number(e.target.value))}
            />
            <span>phút</span>
          </div>
          <div style={cfgStyles.rowLine}>
            <span>Chấm sớm nhất: trước ca</span>
            <input
              type="number"
              min={0}
              style={cfgStyles.numInput}
              value={form.early_clockin_tolerance_minutes}
              onChange={(e) =>
                update("early_clockin_tolerance_minutes", Number(e.target.value))
              }
            />
            <span>phút</span>
          </div>
          <div style={cfgStyles.rowLine}>
            <span>Cảnh báo thiếu chấm công ra: sau tan ca</span>
            <input
              type="number"
              min={0}
              style={cfgStyles.numInput}
              value={form.warn_missing_checkout_after_minutes}
              onChange={(e) =>
                update("warn_missing_checkout_after_minutes", Number(e.target.value))
              }
            />
            <span>phút</span>
          </div>
        </div>
      </div>

      {/* [7] Dung sai xác định trạng thái — BS-400 §2 CFG-20/21/22 (CFG-23 bỏ,
          CHỐT C2 2026-08-25). Giá trị MẶC ĐỊNH TOÀN TENANT, áp cho mọi cơ sở
          CHƯA được cấp quyền "Dung sai riêng" ở khối [9] dưới. */}
      <div style={cfgStyles.card}>
        <div style={cfgStyles.cardTitle}>
          Dung sai xác định trạng thái
          <InfoTip text="Áp dụng cho cơ sở chưa được cấp quyền tự đặt riêng." />
        </div>
        <div className="cfg-field-rows">
          <div style={cfgStyles.rowLine}>
            <span>Dung sai check-in:</span>
            <input
              type="number"
              min={1}
              style={cfgStyles.numInput}
              value={form.check_in_tolerance_minutes}
              onChange={(e) =>
                update("check_in_tolerance_minutes", Number(e.target.value))
              }
            />
            <span>phút</span>
          </div>
          <div style={cfgStyles.rowLine}>
            <span>Dung sai check-out (2 phía):</span>
            <input
              type="number"
              min={1}
              style={cfgStyles.numInput}
              value={form.check_out_early_tolerance_minutes}
              onChange={(e) =>
                update("check_out_early_tolerance_minutes", Number(e.target.value))
              }
            />
            <span>phút</span>
          </div>
          <div style={cfgStyles.rowLine}>
            <span>Ngưỡng quên chấm vào:</span>
            <input
              type="number"
              min={1}
              style={cfgStyles.numInput}
              value={form.missed_check_in_threshold_minutes}
              onChange={(e) =>
                update("missed_check_in_threshold_minutes", Number(e.target.value))
              }
            />
            <span>phút</span>
          </div>
        </div>
        {toleranceErr && (
          <div style={cfgStyles.fieldError}>{toleranceErr}</div>
        )}
      </div>

      {/* [8] Tự động ghi nhận công (theo cơ sở) — BS-400 §7.2 Tầng 1, CFG-24
          (amend 2026-08-18, defaults superseded quyết định A 2026-08-20 +
          §10-Q8 đóng 2026-08-25). 4 công tắc ĐỘC LẬP, Tenant Admin đặt
          baseline TENANT-WIDE, áp cho mọi cơ sở CHƯA được cấp quyền tự cấu
          hình riêng ở khối [9] dưới (đã build, DD-0105). Giá trị khởi tạo
          giờ là ☐☐☐☐ (THỦ CÔNG toàn bộ) — thay đổi có chủ đích tác động
          production đã xác nhận ở §10-Q8, không phải giữ nguyên hardcode cũ. */}
      <div style={cfgStyles.card}>
        <div style={cfgStyles.cardTitle}>
          Tự động ghi nhận công (theo cơ sở)
          <InfoTip text="Áp dụng cho cơ sở chưa được cấp quyền tự bật/tắt riêng." />
        </div>
        <label style={cfgStyles.check}>
          <input
            type="checkbox"
            checked={form.auto_record_missed_check_out}
            onChange={(e) => update("auto_record_missed_check_out", e.target.checked)}
          />
          <span>Quên chấm ra — tự động ghi 0h</span>
        </label>
        <label style={cfgStyles.check}>
          <input
            type="checkbox"
            checked={form.auto_record_off_schedule}
            onChange={(e) => update("auto_record_off_schedule", e.target.checked)}
          />
          <span>Chấm công không theo ca — tự động ghi nhận</span>
        </label>
        <label style={cfgStyles.check}>
          <input
            type="checkbox"
            checked={form.auto_record_missing}
            onChange={(e) => update("auto_record_missing", e.target.checked)}
          />
          <span>Thiếu chấm công — tự động ghi nhận</span>
          <InfoTip warn text="Đã lưu nhưng chưa có hiệu lực — tính năng đang hoàn thiện." />
        </label>
        <label style={cfgStyles.check}>
          <input
            type="checkbox"
            checked={form.auto_record_unmatched}
            onChange={(e) => update("auto_record_unmatched", e.target.checked)}
          />
          <span>Không khớp ca nào — tự động ghi nhận</span>
          <InfoTip warn text="Đã lưu nhưng chưa có hiệu lực — tính năng đang hoàn thiện." />
        </label>
      </div>

      {/* [9] Quyền tự cấu hình theo cơ sở — DD-0105 (2026-08-25), bảng
          `branch_workforce_config_overrides` giờ đã có (Tầng 2). API:
          GET/PUT /api/v2/tenant/branch-workforce-overrides.
          Lưu Ý (deviation có chủ đích): registry ghi "gộp vào cùng cơ chế
          DIRTY/Lưu thay đổi của cả tab" — khối này build với nút Lưu RIÊNG
          (self-contained load/save) thay vì gộp vào `AttendanceForm` chung,
          vì danh sách cơ sở là mảng động (N dòng) khác cấu trúc single-object
          của phần còn lại của tab — tránh rework lớn state hiện có. Hành vi
          batch-save (không live-apply mỗi toggle) vẫn giữ đúng tinh thần gốc. */}
      <BranchOverridesCard />

      {/* [5] Hủy · [6] Lưu thay đổi (disabled tới khi DIRTY) */}
      <div style={cfgStyles.actions}>
        <button
          type="button"
          style={cfgStyles.btnSecondary}
          onClick={handleCancel}
          disabled={!dirty || saving}
        >
          Hủy
        </button>
        <button
          type="button"
          style={cfgStyles.btnPrimary}
          onClick={handleSave}
          disabled={!dirty || saving}
          aria-disabled={!dirty || saving}
        >
          {saving && <span style={cfgStyles.spinner} aria-hidden="true" />}
          <span>Lưu thay đổi</span>
        </button>
      </div>

      {toast && <div style={cfgStyles.toast}>{toast}</div>}
    </div>
  );
};

// =============================================================================
// SC-005-SCHEDULE-CONFIG — tab "Lịch"
// =============================================================================

interface ScheduleForm {
  schedule_cycle: "week" | "month";
  week_start_day: number; // 1=Thứ 2 … 7=Chủ nhật
  publish_ahead_days: number;
  min_rest_between_shifts_hours: number;
  max_hours_week: number;
  max_overtime_day: number;
  max_overtime_month: number;
  max_overtime_year: number;
  min_rest_days_week: number;
  violation_handling: "block" | "warn";
}

// Mặc định theo mockup settings-schedule.html + WP-6a defaults (48h/tuần — Điều 105,
// nghỉ 12h giữa 2 ca — Điều 110, OT tháng/năm — Điều 107, Chặn mặc định)
// ISS-PILOT-17 (2026-08-09): max_overtime_day sửa 3→4 — số "3" KHÔNG có căn cứ
// pháp lý nào (khác các field khác trong object này đều trích đúng Điều luật),
// mâu thuẫn với BR-ATTN-004-LABOR-LAW-2019-COMPLIANCE.md §4 "HARD CONSTRAINTS —
// không thể override: max_daily_ot: 4" + SCHEDULING-BUSINESS-RULES.md §1.3 (Điều
// 107) + tools/scripts/vietnamese-fnb-workflow-test.py — cả 3 nguồn độc lập đều
// ghi 4, không nguồn nào ghi 3. min_rest_days_week=1 xác nhận ĐÚNG qua
// BR-SCHD-006-labor-law-article-110.md §2.4 + SCHEDULING-BUSINESS-RULES.md §1.4
// (Điều 111 — nghỉ hằng tuần), giữ nguyên.
const SCHEDULE_DEFAULTS: ScheduleForm = {
  schedule_cycle: "week",
  week_start_day: 1,
  publish_ahead_days: 3,
  min_rest_between_shifts_hours: 12,
  max_hours_week: 48,
  max_overtime_day: 4,
  max_overtime_month: 40,
  max_overtime_year: 200,
  min_rest_days_week: 1,
  violation_handling: "block",
};

const WEEK_START_OPTIONS = [
  { value: 1, label: "Thứ 2" },
  { value: 2, label: "Thứ 3" },
  { value: 3, label: "Thứ 4" },
  { value: 4, label: "Thứ 5" },
  { value: 5, label: "Thứ 6" },
  { value: 6, label: "Thứ 7" },
  { value: 7, label: "Chủ nhật" },
];

// BOT-039 (#807), 2026-09-12 (ported từ #868/#878 vào nhánh reconcile 2026-09-16
// — xem ghi chú "RECONCILED" ở đầu backend/app/api/v2/tenant.py cho bối cảnh
// đầy đủ): trần tăng ca phải tăng dần ngày ≤ tháng ≤ năm — tách thành hàm thuần
// (export được, test trực tiếp không cần render) vì handleSave() cũ chỉ check
// month<day || year<day, thiếu hẳn year<month (case đã lọt UAT). Bắt qua 2
// bước tuần tự là đủ phủ toàn bộ trường hợp: nếu year<day thật sự xảy ra, một
// trong 2 bước dưới đây luôn bắt được nó trước (day<=month<=year là chuỗi bắc
// cầu). Đây là validate CỨNG (chặn Save) cho dữ liệu vô lý về thứ tự — khác
// bản chất với "vượt trần khuyến nghị BR-ATTN-004 §4.0" (đã có backend
// `legal_warnings`/LegalWarning ở trên, warning-only, không chặn) — 2 khái
// niệm độc lập, không hàm nào thay thế được hàm kia.
export function getOvertimeCeilingError(
  maxOvertimeDay: number,
  maxOvertimeMonth: number,
  maxOvertimeYear: number
): string | null {
  if (maxOvertimeMonth < maxOvertimeDay) {
    return "Trần tăng ca theo tháng phải lớn hơn theo ngày";
  }
  if (maxOvertimeYear < maxOvertimeMonth) {
    return "Trần tăng ca theo năm phải lớn hơn theo tháng";
  }
  return null;
}

/**
 * Tìm cảnh báo (nếu có) cho một field cụ thể trong danh sách `legal_warnings`
 * mà backend trả về (GET/PUT /api/v2/tenant/config). Hàm thuần — tách ra để
 * test trực tiếp không cần render component, cùng pattern với
 * `getOvertimeCeilingError` ở trên.
 *
 * BOT-041 (#810), 2026-09-14→2026-09-17 (reconcile #943): thay cho
 * `getScheduleLegalConstraintWarnings` (map 1 field→1 cảnh báo, tự tính ở FE —
 * đã gỡ, xem note trên ScheduleConfigTab). Backend trả ARRAY (0..N cảnh báo,
 * mỗi field tối đa 1 vì `_SCHEDULE_LEGAL_CONSTRAINT_BOUNDS` backend chỉ có 1
 * entry/field) nên .find luôn khớp tối đa 1 phần tử.
 */
export function getLegalWarningMessage(
  warnings: LegalWarning[],
  field: string
): string | undefined {
  return warnings.find((w) => w.field === field)?.message_vi;
}

// BOT-041 (#810), 2026-09-14→2026-09-17 (reconcile #943): mức mặc định 12/1/48/4/
// 40/200 theo BR-ATTN-004 §4.0 giờ được backend tính + trả về nguyên văn qua
// `legal_warnings` (GET/PUT /api/v2/tenant/config, xem `LegalWarning` interface
// + `getLegalWarning()` ở dưới) — bản client-side `getScheduleLegalConstraintWarnings()`
// từng có ở đây (v2.1.0/v2.2.0 sớm) đã GỠ: nó tự map field→điều luật ở FE, đúng
// loại drift đã làm SCHEDULE_DEFAULTS.max_overtime_day lệch khỏi BR mà không ai
// phát hiện (và tự ghi "Điều 104" SAI cho max_hours_week — backend đã đính chính
// thành "Điều 105", xem RECONCILED note đầu backend/app/api/v2/tenant.py). Một
// nguồn sự thật duy nhất (backend) cho nội dung + điều luật cảnh báo.
//
// Exported (cùng lý do `getScheduleLegalConstraintWarnings` ở trên đã export)
// để BOT-042 (#817) test render trực tiếp — tránh phải dựng toàn bộ trang
// TenantSettings (nhiều tab/API khác không liên quan tới cảnh báo này).
export const ScheduleConfigTab: React.FC<{
  onDirtyChange?: (dirty: boolean) => void;
}> = ({ onDirtyChange }) => {
  const [form, setForm] = useState<ScheduleForm>(SCHEDULE_DEFAULTS);
  const [snapshot, setSnapshot] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishErr, setPublishErr] = useState(false);
  // BOT-039 (#807): string|null (không phải boolean) — hiện đúng thông điệp lỗi
  // cụ thể (tháng<ngày hay năm<tháng) thay vì 1 câu chung chung. Xem
  // getOvertimeCeilingError() ở trên.
  const [otErr, setOtErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // @cto review PR #943 mục 3: "nếu quyết định là cảnh báo thay vì chặn thì
  // cảnh báo phải tới được người đặt". Nạp lúc mở màn hình (GET) và cập nhật
  // sau mỗi lần Lưu (PUT) — KHÔNG chặn thao tác nào.
  const [legalWarnings, setLegalWarnings] = useState<LegalWarning[]>([]);

  // Lookup theo field cho cảnh báo inline ngay dưới từng input (bên cạnh banner
  // tổng ở trên) — xem `getLegalWarningMessage()` (hàm thuần, exported + test ở
  // TenantSettings.bot041LegalBounds.test.ts) cho lý do vì sao đây là .find trên
  // array thay vì map 1 field→1 cảnh báo như bản client-side cũ đã gỡ.
  const getLegalWarning = (field: string): string | undefined =>
    getLegalWarningMessage(legalWarnings, field);

  const dirty = snapshot !== null && JSON.stringify(form) !== snapshot;

  // Báo DIRTY lên parent → đổi tab hiện dialog xác nhận (mockup modalDirty)
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // Toast tự ẩn sau 4s (pattern window.nqhToast trong mockup common.js)
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const cfg = await fetchTenantConfig();
      setLegalWarnings(cfg.legal_warnings || []);
      const ws = cfg.workforce_settings || {};
      const lr = ws.labor_rules || {};
      const loaded: ScheduleForm = {
        schedule_cycle: lr.schedule_cycle ?? SCHEDULE_DEFAULTS.schedule_cycle,
        week_start_day: lr.week_start_day ?? SCHEDULE_DEFAULTS.week_start_day,
        publish_ahead_days: lr.publish_ahead_days ?? SCHEDULE_DEFAULTS.publish_ahead_days,
        min_rest_between_shifts_hours:
          lr.min_rest_between_shifts_hours ?? SCHEDULE_DEFAULTS.min_rest_between_shifts_hours,
        max_hours_week: lr.max_hours_week ?? SCHEDULE_DEFAULTS.max_hours_week,
        max_overtime_day: lr.max_overtime_day ?? SCHEDULE_DEFAULTS.max_overtime_day,
        max_overtime_month: lr.max_overtime_month ?? SCHEDULE_DEFAULTS.max_overtime_month,
        max_overtime_year: lr.max_overtime_year ?? SCHEDULE_DEFAULTS.max_overtime_year,
        min_rest_days_week: lr.min_rest_days_week ?? SCHEDULE_DEFAULTS.min_rest_days_week,
        violation_handling: lr.violation_handling ?? SCHEDULE_DEFAULTS.violation_handling,
      };
      setForm(loaded);
      setSnapshot(JSON.stringify(loaded));
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = <K extends keyof ScheduleForm>(key: K, value: ScheduleForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // [5] Hủy — khôi phục giá trị đã tải/lưu gần nhất
  const handleCancel = () => {
    if (snapshot) setForm(JSON.parse(snapshot));
    setPublishErr(false);
    setOtErr(null);
  };

  // [6] Lưu → VALIDATE (công bố trước ≥0; trần ngày≤tháng≤năm) → SAVING → SAVED:
  // toast "Đã lưu cấu hình lịch làm việc". Ghi labor_rules.violation_handling
  // = "block"/"warn" (WP-6a đọc key này trong workforce_settings).
  //
  // BOT-039 (#807): dùng getOvertimeCeilingError() (kiểm tuần tự month<day rồi
  // year<month) thay vì check cũ `month<day || year<day` — check cũ THIẾU hẳn
  // case year<month (đã lọt UAT). Đây là validate CỨNG chặn Save, độc lập với
  // `legalWarnings` (backend, warning-only) ở trên.
  const handleSave = async () => {
    const badPublish =
      Number.isNaN(form.publish_ahead_days) || form.publish_ahead_days < 0;
    const otError = getOvertimeCeilingError(
      form.max_overtime_day,
      form.max_overtime_month,
      form.max_overtime_year
    );
    setPublishErr(badPublish);
    setOtErr(otError);
    if (badPublish || otError) return;

    setSaving(true);
    try {
      const saved = await saveWorkforceSection("labor_rules", { ...form });
      setSnapshot(JSON.stringify(form));
      // Cảnh báo pháp lý (không chặn) — hiển thị ngay dưới tiêu đề, KHÔNG
      // nhét vào toast 4 giây: người đặt cần đọc được điều luật + ngưỡng.
      const warnings = saved.legal_warnings || [];
      setLegalWarnings(warnings);
      setToast(
        warnings.length > 0
          ? `Đã lưu — có ${warnings.length} cảnh báo ràng buộc pháp lý`
          : "Đã lưu cấu hình lịch làm việc"
      );
    } catch {
      setToast("Lỗi lưu cấu hình — thử lại");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.card}>
        <p style={{ color: "var(--nqh-gray-500, #6b7280)" }}>Đang tải cấu hình lịch làm việc...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={styles.card}>
        <p style={{ color: "var(--nqh-gray-500, #6b7280)" }}>Không tải được cấu hình. Thử lại</p>
        <button style={cfgStyles.btnPrimary} onClick={load}>
          Thử lại
        </button>
      </div>
    );
  }

  return (
    <div>
      <style>{CFG_SPIN_STYLE}{CFG_GRID_STYLE}</style>
      <h3 style={cfgStyles.sectionTitle}>Cấu hình Lịch làm việc</h3>

      {/* Cảnh báo ràng buộc pháp lý ngoài khung khuyến nghị (BR-ATTN-004 §4.0).
          KHÔNG chặn lưu — giá trị ĐÃ persist; đây là thông báo để người đặt biết
          mình vừa đặt gì và theo điều luật nào (@cto review PR #943 mục 3).
          role="status" (không phải "alert"): thông báo, không phải lỗi chặn. */}
      {legalWarnings.length > 0 && (
        <div
          role="status"
          aria-live="polite"
          data-testid="labor-rules-legal-warnings"
          style={{
            border: "1px solid var(--nqh-warning-300, #fcd34d)",
            background: "var(--nqh-warning-50, #fffbeb)",
            color: "var(--nqh-gray-900, #1f2937)",
            borderRadius: "12px",
            padding: "12px 16px",
            marginBottom: "16px",
            fontSize: "14px",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: "6px" }}>
            ⚠️ {legalWarnings.length} cảnh báo ràng buộc pháp lý — cấu hình vẫn
            được lưu
          </div>
          <ul style={{ margin: 0, paddingLeft: "20px" }}>
            {legalWarnings.map((w) => (
              <li key={w.field} style={{ marginBottom: "4px" }}>
                {w.message_vi}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* [3] Chu kỳ & công bố lịch */}
      <div style={cfgStyles.card}>
        <div style={cfgStyles.cardTitle}>Chu kỳ &amp; công bố lịch</div>
        <div style={cfgStyles.label}>Chu kỳ lập lịch:</div>
        <div style={cfgStyles.radios} role="radiogroup" aria-label="Chu kỳ lập lịch">
          <label style={cfgStyles.radioLabel}>
            <input
              type="radio"
              name="cycle"
              checked={form.schedule_cycle === "week"}
              onChange={() => update("schedule_cycle", "week")}
            />
            <span>Hàng tuần</span>
          </label>
          {/* #817 (CEO 04/10, UI-UX rule 14): the «Hàng tháng» option is hidden in the
              pilot because no monthly grid exists. It is still shown when a tenant already
              saved "month", so an admin can see it and switch back to «Hàng tuần». */}
          {form.schedule_cycle === "month" && (
          <label style={cfgStyles.radioLabel}>
            <input
              type="radio"
              name="cycle"
              checked={form.schedule_cycle === "month"}
              onChange={() => update("schedule_cycle", "month")}
            />
            <span>Hàng tháng</span>
            {/* BOT-042 (#817), 2026-09-14: backend đọc đúng giá trị này
                (ScheduleRulesInfo.schedule_cycle, manager_schedule.py) nhưng
                Bảng phân ca (ManagerSchedulePage.tsx) CHƯA có UI lưới-theo-tháng
                — chọn "Hàng tháng" lưu thành công nhưng không đổi gì ở màn Quản
                lý xem. Cùng pattern/văn bản đã dùng cho auto_record_missing/
                auto_record_unmatched phía trên (dòng ~1777/1786) — "lưu được,
                chưa có hiệu lực" là tình huống lặp lại trong file này, giữ
                nguyên văn để nhất quán. KHÔNG disable radio: giá trị vẫn hợp lệ
                phía backend, TA có thể cấu hình trước cho lúc UI tháng ra mắt. */}
            <InfoTip warn text="Đã lưu nhưng chưa có hiệu lực — tính năng đang hoàn thiện." />
          </label>
          )}
        </div>
        {/* ISS-PILOT-24 REOPEN: 2 field ngắn → 1 cột = 2 hàng */}
        <div className="cfg-field-rows">
          <div style={cfgStyles.rowLine}>
            <span>Ngày bắt đầu tuần:</span>
            <select
              style={cfgStyles.selectInput}
              value={form.week_start_day}
              onChange={(e) => update("week_start_day", Number(e.target.value))}
            >
              {WEEK_START_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div style={cfgStyles.rowLine}>
            <span>Công bố lịch trước tối thiểu:</span>
            <input
              type="number"
              min={0}
              style={cfgStyles.numInput}
              value={form.publish_ahead_days}
              onChange={(e) => update("publish_ahead_days", Number(e.target.value))}
            />
            <span>ngày</span>
          </div>
        </div>
        {publishErr && (
          <div style={cfgStyles.fieldError}>Số ngày công bố trước phải là số không âm</div>
        )}
      </div>

      {/* [4] Ràng buộc pháp lý khi xếp ca */}
      <div style={cfgStyles.card}>
        <div style={cfgStyles.cardTitle}>Ràng buộc pháp lý khi xếp ca</div>
        {/* ISS-PILOT-24 REOPEN: 3 field ngắn (giờ) → 1 cột = 3 hàng */}
        <div className="cfg-field-rows">
          <div style={cfgStyles.rowLine}>
            <span>Nghỉ tối thiểu giữa 2 ca:</span>
            <input
              type="number"
              min={0}
              style={cfgStyles.numInput}
              value={form.min_rest_between_shifts_hours}
              onChange={(e) =>
                update("min_rest_between_shifts_hours", Number(e.target.value))
              }
            />
            <span>giờ</span>
          </div>
          {getLegalWarning("min_rest_between_shifts_hours") && (
            <div style={cfgStyles.fieldWarning}>{getLegalWarning("min_rest_between_shifts_hours")}</div>
          )}
          <div style={cfgStyles.rowLine}>
            <span>Trần giờ làm thường:</span>
            <input
              type="number"
              min={1}
              style={cfgStyles.numInput}
              value={form.max_hours_week}
              onChange={(e) => update("max_hours_week", Number(e.target.value))}
            />
            <span>giờ/tuần</span>
          </div>
          {getLegalWarning("max_hours_week") && (
            <div style={cfgStyles.fieldWarning}>{getLegalWarning("max_hours_week")}</div>
          )}
          <div style={cfgStyles.rowLine}>
            <span>Nghỉ tối thiểu:</span>
            <input
              type="number"
              min={0}
              style={cfgStyles.numInput}
              value={form.min_rest_days_week}
              onChange={(e) => update("min_rest_days_week", Number(e.target.value))}
            />
            <span>ngày/tuần</span>
          </div>
          {getLegalWarning("min_rest_days_week") && (
            <div style={cfgStyles.fieldWarning}>{getLegalWarning("min_rest_days_week")}</div>
          )}
        </div>
        <div style={cfgStyles.label}>Trần tăng ca:</div>
        {/* ISS-PILOT-24 REOPEN: 3 field dùng chung label "Trần tăng ca:" ở trên → 1 cột = 3 hàng */}
        <div className="cfg-field-rows">
          <div style={cfgStyles.rowLine}>
            <input
              type="number"
              min={0}
              style={cfgStyles.numInput}
              value={form.max_overtime_day}
              onChange={(e) => update("max_overtime_day", Number(e.target.value))}
            />
            <span>giờ/ngày</span>
          </div>
          {getLegalWarning("max_overtime_day") && (
            <div style={cfgStyles.fieldWarning}>{getLegalWarning("max_overtime_day")}</div>
          )}
          <div style={cfgStyles.rowLine}>
            <input
              type="number"
              min={0}
              style={cfgStyles.numInput}
              value={form.max_overtime_month}
              onChange={(e) => update("max_overtime_month", Number(e.target.value))}
            />
            <span>giờ/tháng</span>
          </div>
          {getLegalWarning("max_overtime_month") && (
            <div style={cfgStyles.fieldWarning}>{getLegalWarning("max_overtime_month")}</div>
          )}
          <div style={cfgStyles.rowLine}>
            <input
              type="number"
              min={0}
              style={cfgStyles.numInput}
              value={form.max_overtime_year}
              onChange={(e) => update("max_overtime_year", Number(e.target.value))}
            />
            <span>giờ/năm</span>
          </div>
          {getLegalWarning("max_overtime_year") && (
            <div style={cfgStyles.fieldWarning}>{getLegalWarning("max_overtime_year")}</div>
          )}
        </div>
        {otErr && (
          <div style={cfgStyles.fieldError}>
            {otErr}
          </div>
        )}
        <div style={cfgStyles.label}>Khi lịch vi phạm ràng buộc:</div>
        <div style={cfgStyles.radios} role="radiogroup" aria-label="Khi lịch vi phạm ràng buộc">
          <label style={cfgStyles.radioLabel}>
            <input
              type="radio"
              name="violation"
              checked={form.violation_handling === "block"}
              onChange={() => update("violation_handling", "block")}
            />
            <span>Chặn, không cho lưu lịch</span>
          </label>
          <label style={cfgStyles.radioLabel}>
            <input
              type="radio"
              name="violation"
              checked={form.violation_handling === "warn"}
              onChange={() => update("violation_handling", "warn")}
            />
            <span>Chỉ cảnh báo, vẫn cho lưu</span>
          </label>
        </div>
      </div>

      {/* [5] Hủy · [6] Lưu thay đổi (disabled tới khi DIRTY) */}
      <div style={cfgStyles.actions}>
        <button
          type="button"
          style={cfgStyles.btnSecondary}
          onClick={handleCancel}
          disabled={!dirty || saving}
        >
          Hủy
        </button>
        <button
          type="button"
          style={cfgStyles.btnPrimary}
          onClick={handleSave}
          disabled={!dirty || saving}
          aria-disabled={!dirty || saving}
        >
          {saving && <span style={cfgStyles.spinner} aria-hidden="true" />}
          <span>Lưu thay đổi</span>
        </button>
      </div>

      {toast && <div style={cfgStyles.toast}>{toast}</div>}
    </div>
  );
};

const LatePolicyTab: React.FC<{
  settings: TenantSettingsData;
  onChange: (key: string, value: any) => void;
}> = ({ settings, onChange }) => {
  const tiers = [
    {
      label: "T0 (Không phạt)",
      minuteKey: "late_t0_max_minutes",
      penaltyKey: null,
      value: settings.late_t0_max_minutes,
    },
    {
      label: "T1",
      minuteKey: "late_t1_max_minutes",
      penaltyKey: "late_t1_penalty_vnd",
      value: settings.late_t1_max_minutes,
      penalty: settings.late_t1_penalty_vnd,
    },
    {
      label: "T2",
      minuteKey: "late_t2_max_minutes",
      penaltyKey: "late_t2_penalty_vnd",
      value: settings.late_t2_max_minutes,
      penalty: settings.late_t2_penalty_vnd,
    },
    {
      label: "T3 (Vi phạm nghiêm trọng)",
      minuteKey: null,
      penaltyKey: "late_t3_penalty_vnd",
      penalty: settings.late_t3_penalty_vnd,
    },
  ];

  const formatVND = (v: number) =>
    new Intl.NumberFormat("vi-VN").format(v) + "\u20AB";

  const previewParts: string[] = [];
  previewParts.push(`0-${settings.late_t0_max_minutes} phút: Không phạt`);
  previewParts.push(
    `${settings.late_t0_max_minutes + 1}-${settings.late_t1_max_minutes} phút: ${formatVND(settings.late_t1_penalty_vnd)}`
  );
  previewParts.push(
    `${settings.late_t1_max_minutes + 1}-${settings.late_t2_max_minutes} phút: ${formatVND(settings.late_t2_penalty_vnd)}`
  );
  previewParts.push(
    `>${settings.late_t2_max_minutes} phút: ${formatVND(settings.late_t3_penalty_vnd)}`
  );

  return (
    <div style={styles.card}>
      <h3 style={{ margin: "0 0 20px 0", fontSize: "16px", color: "#1f2937" }}>
        Chính sách đi trễ
      </h3>

      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "20px" }}>
        <thead>
          <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
            <th style={{ textAlign: "left", padding: "8px", fontSize: "13px", color: "#6b7280" }}>
              Mức
            </th>
            <th style={{ textAlign: "left", padding: "8px", fontSize: "13px", color: "#6b7280" }}>
              Phút tối đa
            </th>
            <th style={{ textAlign: "left", padding: "8px", fontSize: "13px", color: "#6b7280" }}>
              Phạt (VND)
            </th>
          </tr>
        </thead>
        <tbody>
          {tiers.map((tier, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
              <td style={{ padding: "10px 8px", fontSize: "14px", fontWeight: "500" }}>
                {tier.label}
              </td>
              <td style={{ padding: "10px 8px" }}>
                {tier.minuteKey ? (
                  <input
                    type="number"
                    min={0}
                    max={120}
                    value={tier.value || 0}
                    onChange={(e) => onChange(tier.minuteKey!, Number(e.target.value))}
                    style={{ ...styles.input, width: "100px" }}
                  />
                ) : (
                  <span style={{ fontSize: "13px", color: "#9ca3af" }}>—</span>
                )}
              </td>
              <td style={{ padding: "10px 8px" }}>
                {tier.penaltyKey ? (
                  <input
                    type="number"
                    min={0}
                    step={10000}
                    value={tier.penalty || 0}
                    onChange={(e) => onChange(tier.penaltyKey!, Number(e.target.value))}
                    style={{ ...styles.input, width: "140px" }}
                  />
                ) : (
                  <span style={{ fontSize: "13px", color: "#9ca3af" }}>—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div
        style={{
          background: "#f0fdf4",
          border: "1px solid #bbf7d0",
          borderRadius: "8px",
          padding: "12px 16px",
          fontSize: "13px",
          color: "#166534",
        }}
      >
        <strong>Xem trước:</strong> {previewParts.join(" | ")}
      </div>
    </div>
  );
};

const ChecklistTab: React.FC = () => {
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDept, setNewDept] = useState("");
  const [newShiftType, setNewShiftType] = useState<"PRE_SHIFT" | "POST_SHIFT">("PRE_SHIFT");
  const [newItems, setNewItems] = useState<ChecklistItem[]>([
    { name: "", order: 1, is_mandatory: true, requires_photo: false, requires_note: false },
  ]);

  const fetchTemplates = useCallback(() => {
    setLoading(true);
    apiClient
      .get("/tenant-admin/checklist-templates", { baseURL: getApiBase("v2") })
      .then((res) => setTemplates(res.data || []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const createTemplate = async () => {
    if (!newName.trim()) return;
    const validItems = newItems.filter((item) => item.name.trim());
    if (validItems.length === 0) return;

    try {
      await apiClient.post("/tenant-admin/checklist-templates", {
        name: newName,
        department: newDept || null,
        shift_type: newShiftType,
        items: validItems,
      }, { baseURL: getApiBase("v2") });
      setShowCreate(false);
      setNewName("");
      setNewDept("");
      setNewItems([
        { name: "", order: 1, is_mandatory: true, requires_photo: false, requires_note: false },
      ]);
      fetchTemplates();
    } catch {
      // Error displayed by API interceptor
    }
  };

  const deleteTemplate = async (id: string) => {
    try {
      await apiClient.delete(`/tenant-admin/checklist-templates/${id}`, { baseURL: getApiBase("v2") });
      fetchTemplates();
    } catch {
      // Silent
    }
  };

  const addItem = () => {
    setNewItems((prev) => [
      ...prev,
      {
        name: "",
        order: prev.length + 1,
        is_mandatory: true,
        requires_photo: false,
        requires_note: false,
      },
    ]);
  };

  const updateItem = (index: number, field: string, value: any) => {
    setNewItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const removeItem = (index: number) => {
    setNewItems((prev) => prev.filter((_, i) => i !== index).map((item, i) => ({ ...item, order: i + 1 })));
  };

  if (loading) {
    return (
      <div style={styles.card}>
        <p style={{ color: "#6b7280" }}>Đang tải mẫu checklist...</p>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h3 style={{ margin: 0, fontSize: "16px", color: "#1f2937" }}>
          Mẫu checklist ({templates.length})
        </h3>
        <button style={styles.btn("primary")} onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? "Hủy" : "+ Tạo mẫu mới"}
        </button>
      </div>

      {showCreate && (
        <div
          style={{
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "20px",
            background: "#f9fafb",
          }}
        >
          <div style={styles.row}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Tên mẫu</label>
              <input
                style={styles.input}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="VD: Checklist mở ca sáng"
              />
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Bộ phận</label>
              <input
                style={styles.input}
                value={newDept}
                onChange={(e) => setNewDept(e.target.value)}
                placeholder="VD: Bếp, Pha chế"
              />
            </div>
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Loại ca</label>
            <select
              style={{ ...styles.select, width: "200px" }}
              value={newShiftType}
              onChange={(e) => setNewShiftType(e.target.value as "PRE_SHIFT" | "POST_SHIFT")}
            >
              <option value="PRE_SHIFT">Trước ca (PRE_SHIFT)</option>
              <option value="POST_SHIFT">Sau ca (POST_SHIFT)</option>
            </select>
          </div>

          <div style={{ marginBottom: "12px" }}>
            <label style={styles.label}>Danh sách mục (tối đa 50)</label>
            {newItems.map((item, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                  marginBottom: "8px",
                  flexWrap: "wrap",
                }}
              >
                <span style={{ fontSize: "13px", color: "#6b7280", width: "24px" }}>
                  #{i + 1}
                </span>
                <input
                  style={{ ...styles.input, flex: 1, minWidth: "200px" }}
                  value={item.name}
                  onChange={(e) => updateItem(i, "name", e.target.value)}
                  placeholder="Tên mục checklist"
                />
                <label style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" }}>
                  <input
                    type="checkbox"
                    checked={item.is_mandatory}
                    onChange={(e) => updateItem(i, "is_mandatory", e.target.checked)}
                  />
                  Bắt buộc
                </label>
                <label style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" }}>
                  <input
                    type="checkbox"
                    checked={item.requires_photo}
                    onChange={(e) => updateItem(i, "requires_photo", e.target.checked)}
                  />
                  Ảnh
                </label>
                <label style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" }}>
                  <input
                    type="checkbox"
                    checked={item.requires_note}
                    onChange={(e) => updateItem(i, "requires_note", e.target.checked)}
                  />
                  Ghi chú
                </label>
                {newItems.length > 1 && (
                  <button
                    style={{ ...styles.btn("danger"), padding: "4px 8px", fontSize: "12px" }}
                    onClick={() => removeItem(i)}
                  >
                    Xóa
                  </button>
                )}
              </div>
            ))}
            {newItems.length < 50 && (
              <button
                style={styles.btn("secondary")}
                onClick={addItem}
              >
                + Thêm mục
              </button>
            )}
          </div>

          <button style={styles.btn("primary")} onClick={createTemplate}>
            Tạo mẫu checklist
          </button>
        </div>
      )}

      {templates.length === 0 && !showCreate ? (
        <p style={{ color: "#6b7280", fontSize: "14px" }}>
          Chưa có mẫu checklist nào. Bấm "Tạo mẫu mới" để bắt đầu.
        </p>
      ) : (
        templates.map((tpl) => (
          <div
            key={tpl.id}
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              marginBottom: "12px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 16px",
                background: "#f9fafb",
                cursor: "pointer",
              }}
              onClick={() => toggleExpand(tpl.id)}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "12px" }}>
                  {expanded.has(tpl.id) ? "\u25BC" : "\u25B6"}
                </span>
                <strong style={{ fontSize: "14px" }}>{tpl.name}</strong>
                <span style={styles.chip("green")}>
                  {tpl.shift_type === "PRE_SHIFT" ? "Trước ca" : "Sau ca"}
                </span>
                {tpl.department && (
                  <span style={styles.chip("blue")}>{tpl.department}</span>
                )}
                <span style={{ fontSize: "12px", color: "#9ca3af" }}>
                  {tpl.items?.length || 0} mục
                </span>
              </div>
              <button
                style={{ ...styles.btn("danger"), padding: "4px 10px", fontSize: "12px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  deleteTemplate(tpl.id);
                }}
              >
                Xóa
              </button>
            </div>
            {expanded.has(tpl.id) && tpl.items && (
              <div style={{ padding: "0 16px 12px" }}>
                {tpl.items.map((item, i) => (
                  <div
                    key={item.id || i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "8px 0",
                      borderBottom: i < tpl.items.length - 1 ? "1px solid #f3f4f6" : "none",
                      fontSize: "13px",
                    }}
                  >
                    <span style={{ color: "#9ca3af", width: "24px" }}>#{item.item_order}</span>
                    <span style={{ flex: 1 }}>{item.title_vi}</span>
                    {item.is_mandatory && <span style={styles.chip("green")}>Bắt buộc</span>}
                    {item.requires_photo && <span style={styles.chip("blue")}>Ảnh</span>}
                    {item.requires_note && <span style={styles.chip("blue")}>Ghi chú</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
};

// =============================================================================
// Main Component
// =============================================================================

// ISS-0019 (2026-07-30, @coder_frontend) theo báo cáo UI divergence @reviewer §5 (bản cục bộ, KHÔNG có trong repo)
// + quyết định OQ-3: thanh tab rút về đúng 4 tab registry SC-003 L595-597
// (Mã ca mặc định · Chấm công · Lịch · Loại đơn vị), nhãn đúng spec.
// 5 tab ngoài scope v0 (Thông tin chung · Thương hiệu · Cơ cấu tổ chức ·
// Chính sách đi trễ [xung đột BS-011-X01] · Mẫu checklist) ẨN khỏi thanh tab —
// component + nhánh render giữ nguyên trong code (không xoá) để mở lại khi
// @cpo mở scope release sau; TabKey giữ đủ để không phá type của các tab ẩn.
type TabKey =
  | "general"
  | "branding"
  | "organization"
  | "attendance"
  | "schedule"
  | "late_policy"
  | "checklist"
  | "shift_types"
  | "unit_types"
  | "branch_geofence"
  | "public_holidays";

// BỔ SUNG (2026-08-12, DD-0099 + BR-TENM-006 FR-012.v0): tab thứ 5 "Vị trí chi
// nhánh" (SC-028-BRANCH-GEOFENCE, Điểm vào 2) — 4 tab gốc giữ nguyên không đổi.
// BỔ SUNG (2026-09-24, SC-033-TA-PUBLIC-HOLIDAYS, BS-033): tab thứ 6 "Ngày
// nghỉ lễ" — theo đúng NAVIGATION wireframe "Tenant Admin → Cài đặt → Ngày
// nghỉ lễ" — cùng pattern tab con self-contained như unit_types/branch_geofence.
const TAB_CONFIG: Array<{ key: TabKey; label: string }> = [
  { key: "shift_types", label: "Mã ca" },
  { key: "attendance", label: "Chấm công" },
  { key: "schedule", label: "Lịch" },
  { key: "unit_types", label: "Loại đơn vị" },
  { key: "branch_geofence", label: "Vị trí chi nhánh" },
  { key: "public_holidays", label: "Ngày nghỉ lễ" },
];

const TenantSettings: React.FC = () => {
  const { user, isAuthenticated, userRole } = useAuthStore();

  // SC-003 STATE DEFAULT (registry L601): mở tab "Mã ca"
  const [activeTab, setActiveTab] = useState<TabKey>("shift_types");
  const [settings, setSettings] = useState<TenantSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // SC-004/005 DIRTY guard (mockup modalDirty): tab Chấm công/Lịch có thay đổi
  // chưa lưu → đổi tab hiện dialog "Bạn có thay đổi chưa lưu. Rời đi?"
  const [dirtyTab, setDirtyTab] = useState<"attendance" | "schedule" | null>(null);
  const [pendingTab, setPendingTab] = useState<TabKey | null>(null);

  // ISS-PILOT-33 regression fix (2026-08-17): title sticky bên dưới chỉ bật ở
  // ≥1024px. Dưới 1024px, TenantAdminLayout.tsx render nút ☰ (position:sticky,
  // top:8px, zIndex:40) đè lên chữ đầu title nếu title cũng sticky/top:0 ở cùng
  // breakpoint — đo trực tiếp @tester xác nhận "Cà" bị che sau cuộn (xem
  // docs/05-test/13-test-issues/RELEASE-V0-ISSUES-CONSOLIDATED-2026-08-09.md §95
  // "Phát hiện 2"). Cùng breakpoint (max-width: 1023px) với TenantAdminLayout để
  // 2 component luôn đồng bộ trạng thái narrow/drawer.
  const [isNarrow, setIsNarrow] = useState<boolean>(
    () => typeof window !== "undefined" && window.innerWidth < 1024
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const onChange = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
    setIsNarrow(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Load settings on mount — zero-mock: lỗi API → error state + retry (KHÔNG fallback hardcode)
  const loadSettings = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    apiClient
      .get("/tenant-admin/settings", { baseURL: getApiBase("v2") })
      .then((res) => setSettings(res.data))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleAttendanceDirty = useCallback(
    (d: boolean) => setDirtyTab(d ? "attendance" : null),
    []
  );
  const handleScheduleDirty = useCallback(
    (d: boolean) => setDirtyTab(d ? "schedule" : null),
    []
  );

  const handleTabSelect = (key: TabKey) => {
    if (dirtyTab && key !== activeTab) {
      setPendingTab(key);
      return;
    }
    setActiveTab(key);
  };

  // Dialog DIRTY: [Rời] → bỏ thay đổi, chuyển tab; [Ở lại] → đóng dialog
  const confirmLeaveTab = () => {
    setDirtyTab(null);
    if (pendingTab) setActiveTab(pendingTab);
    setPendingTab(null);
  };

  // Auto-save with 2s debounce (force=true bypasses debounce for manual save)
  const autoSave = useCallback(
    (updatedSettings: TenantSettingsData, force?: boolean) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      const execute = async () => {
        setSaveStatus("saving");
        setLastSavedAt(null);
        try {
          // For branding, use dedicated endpoint
          const { branding, ...rest } = updatedSettings;
          await apiClient.patch("/tenant-admin/settings", rest, { baseURL: getApiBase("v2") });
          if (branding && Object.keys(branding).length > 0) {
            await apiClient.put("/tenant-admin/settings/branding", { branding }, { baseURL: getApiBase("v2") });
          }
          setSaveStatus("saved");
          setLastSavedAt(new Date());
        } catch {
          setSaveStatus("error");
        }
      };
      if (force) {
        execute();
      } else {
        debounceRef.current = setTimeout(execute, 2000);
      }
    },
    []
  );

  const handleChange = useCallback(
    (key: string, value: any) => {
      setSettings((prev) => {
        if (!prev) return prev;
        const updated = { ...prev, [key]: value };
        setSaveStatus("saving");
        setLastSavedAt(null);
        autoSave(updated);
        return updated;
      });
    },
    [autoSave]
  );

  // CTO F15-FE: Auth guard moved below all hooks to comply with React Rules of Hooks
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }
  if (userRole !== "TENANT_ADMIN" && userRole !== "TENANT_OWNER") {
    return <Navigate to="/dashboard/staff" replace />;
  }

  if (loading) {
    return (
      <div style={styles.page}>
        {/* RS-1 (2026-07-30): gỡ back-nav riêng — header tổ chức nằm trong sidebar
            TenantAdminLayout (ISS-0016 #5); vùng nội dung chỉ có tiêu đề vùng (D3) */}
        <main style={styles.main}>
          <h1 style={styles.title}>Cài đặt tổ chức</h1>
          <div style={styles.card}>
            <p style={{ color: "#6b7280" }}>Đang tải cài đặt...</p>
          </div>
        </main>
      </div>
    );
  }

  // Zero-mock: lỗi tải → error state + nút retry (không fallback giá trị mặc định)
  if (loadError || !settings) {
    return (
      <div style={styles.page}>
        <main style={styles.main}>
          <h1 style={styles.title}>Cài đặt tổ chức</h1>
          <div style={styles.card}>
            <p style={{ color: "#6b7280" }}>Không tải được cấu hình. Thử lại</p>
            <button style={styles.btn("primary")} onClick={loadSettings}>
              Thử lại
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        {/* Tiêu đề vùng nội dung + chỉ báo lưu (RS-1: không còn back-nav riêng) */}
        {/* ISS-PILOT-33: sticky để title không biến mất khi tab "Cơ cấu tổ chức" cuộn dọc.
            marginTop:-24 huỷ padding-top 24px của <main> rồi paddingTop:24 tái lập đúng
            khoảng cách đó BÊN TRONG chính div này — giữ nguyên tổng chiều cao (không đè
            lên nội dung đầu tiên khi chưa cuộn) nhưng khoảng cách đi theo header khi đã stick.
            background trùng styles.page (#f3f4f6, không trong suốt) để che nội dung cuộn qua dưới;
            zIndex:10 đủ nổi trên nội dung tab (z-index mặc định) nhưng thấp hơn mọi modal (zIndex:1000).
            Sticky CHỈ bật ở ≥1024px (isNarrow=false) — dưới 1024px giữ hành vi cũ (không sticky) để
            tránh đè lên nút ☰ (zIndex:40) của TenantAdminLayout.tsx, xem regression ghi ở khai báo
            isNarrow phía trên. */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "20px",
            ...(isNarrow
              ? {}
              : {
                  position: "sticky" as const,
                  top: 0,
                  zIndex: 10,
                  background: "#f3f4f6",
                  marginTop: "-24px",
                  paddingTop: "24px",
                }),
          }}
        >
          <h1 style={styles.title}>Cài đặt tổ chức</h1>
          <span style={styles.saveIndicator}>
            {saveStatus === "saving" && "Đang lưu..."}
            {saveStatus === "saved" && lastSavedAt && `✓ Đã lưu tự động lúc ${formatHHmm(lastSavedAt)}`}
            {saveStatus === "error" && "Lỗi lưu — thử lại"}
          </span>
          {["general", "branding", "late_policy"].includes(activeTab) && (
            <button
              onClick={() => settings && autoSave(settings, true)}
              disabled={saveStatus === "saving"}
              style={styles.saveNowBtn}
              data-testid="save-now-btn"
            >
              Lưu ngay
            </button>
          )}
        </div>

        {/* Tab bar */}
        <div style={styles.tabBar}>
          {TAB_CONFIG.map((tab) => (
            <button
              key={tab.key}
              style={styles.tab(activeTab === tab.key)}
              onClick={() => handleTabSelect(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === "general" && (
          <GeneralTab settings={settings} onChange={handleChange} />
        )}
        {activeTab === "branding" && (
          <BrandingTab settings={settings} onChange={handleChange} />
        )}
        {activeTab === "organization" && <OrgTreeTab />}
        {activeTab === "attendance" && <AttendanceConfigTab onDirtyChange={handleAttendanceDirty} />}
        {activeTab === "schedule" && <ScheduleConfigTab onDirtyChange={handleScheduleDirty} />}
        {activeTab === "late_policy" && (
          <LatePolicyTab settings={settings} onChange={handleChange} />
        )}
        {activeTab === "checklist" && <ChecklistTab />}
        {activeTab === "shift_types" && <ShiftTypesTab />}
        {activeTab === "unit_types" && <UnitTypesTab />}
        {activeTab === "branch_geofence" && <BranchGeofenceTab />}
        {activeTab === "public_holidays" && <PublicHolidaysTab />}
      </main>

      {/* SC-004/005: dialog xác nhận khi đổi tab lúc DIRTY (mockup modalDirty) */}
      {pendingTab && (
        <div style={cfgStyles.overlay} role="dialog" aria-modal="true" aria-labelledby="dirtyTitle">
          <div style={cfgStyles.modal}>
            <h2 style={cfgStyles.modalTitle} id="dirtyTitle">
              Bạn có thay đổi chưa lưu. Rời đi?
            </h2>
            <p style={{ margin: 0, fontSize: "14px", color: "var(--nqh-gray-700, #374151)" }}>
              Các thay đổi trong tab {dirtyTab === "schedule" ? "Lịch" : "Chấm công"} chưa
              được lưu. Rời tab sẽ mất các thay đổi này.
            </p>
            <div style={cfgStyles.modalFoot}>
              <button
                type="button"
                style={cfgStyles.btnSecondary}
                onClick={() => setPendingTab(null)}
              >
                Ở lại
              </button>
              <button type="button" style={cfgStyles.btnPrimary} onClick={confirmLeaveTab}>
                Rời
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * ShiftTypesTab — Quản lý Mã Ca (DD-0031 §3, DD-0033, BR-TENM-006 FR-008)
 * CRUD shift types on shift_templates TABLE via v2 API.
 * Features: source badge, color dot, category label, edit modal, soft delete.
 */

const CATEGORY_LABELS: Record<string, string> = {
  morning: "Sáng", afternoon: "Chiều", evening: "Tối",
  night: "Đêm", split: "Gãy", full_day: "Cả ngày", flexible: "Linh hoạt",
};

const SOURCE_BADGES: Record<string, { label: string; bg: string; color: string }> = {
  manual: { label: "Thủ công", bg: "#f3f4f6", color: "#374151" },
  bflow: { label: "BFlow", bg: "#fef3c7", color: "#92400e" },
  csv: { label: "CSV", bg: "#e0e7ff", color: "#3730a3" },
};

const CATEGORY_OPTIONS = [
  { value: "morning", label: "Sáng" }, { value: "afternoon", label: "Chiều" },
  { value: "evening", label: "Tối" }, { value: "night", label: "Đêm" },
  { value: "split", label: "Gãy" },
];

type ShiftFormData = {
  code: string; name: string; start_time: string; end_time: string;
  category: string; color_code: string; is_overnight: boolean;
};

const EMPTY_FORM: ShiftFormData = { code: "", name: "", start_time: "06:00", end_time: "14:00", category: "morning", color_code: "#3B82F6", is_overnight: false };

const ShiftTypesTab: React.FC = () => {
  const [shiftTypes, setShiftTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [formData, setFormData] = useState<ShiftFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // bot-052 (#842) fix, 2026-09-14: form render Ở TRÊN bảng — bấm "Sửa" một
  // dòng cuối bảng dài mở form ngoài viewport, không có gì kéo mắt người dùng
  // lên trên. openEdit()/openCreate() chỉ set state trước đây, không scroll.
  const formRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (showForm) formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [showForm, editId]);

  const fetchShiftTypes = useCallback(async () => {
    try {
      const res = await apiClient.get("/tenant-admin/shift-types", { baseURL: getApiBase("v2") });
      setShiftTypes(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError("Không thể tải danh sách Mã Ca");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchShiftTypes(); }, [fetchShiftTypes]);

  const openCreate = () => { setEditId(null); setFormData(EMPTY_FORM); setShowForm(true); };

  const openEdit = (st: any) => {
    setEditId(st.id);
    setFormData({
      code: st.code, name: st.name, start_time: st.start_time, end_time: st.end_time,
      category: st.category || "full_day", color_code: st.color_code || "#10B981", is_overnight: st.is_overnight || false,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      if (editId) {
        // PUT — DD-0031 §2.1
        const { code, ...updateData } = formData;
        await apiClient.put(`/tenant-admin/shift-types/${editId}`, updateData, { baseURL: getApiBase("v2") });
      } else {
        // POST — DD-0031 §2.1
        await apiClient.post("/tenant-admin/shift-types", formData, { baseURL: getApiBase("v2") });
      }
      setShowForm(false); setEditId(null); setFormData(EMPTY_FORM);
      await fetchShiftTypes();
    } catch (err: any) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : (editId ? "Cập nhật thất bại" : "Tạo Mã Ca thất bại"));
    } finally { setSaving(false); }
  };

  const handleDelete = async (st: any) => {
    // DD-0033 §2.2: bflow source → cannot delete
    if (st.source === "bflow") return;
    // DD-0031 §3.3: confirm dialog
    const msg = st.is_default
      ? `Mã Ca "${st.name}" là mã mặc định hệ thống. Bạn có chắc chắn muốn xóa?`
      : `Xóa Mã Ca "${st.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await apiClient.delete(`/tenant-admin/shift-types/${st.id}`, { baseURL: getApiBase("v2") });
      await fetchShiftTypes();
    } catch (err: any) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Xóa thất bại");
    }
  };

  // ── DD-0038: BFlow Import state (BEFORE early return — React hooks rule) ──
  const [showBflowImport, setShowBflowImport] = useState(false);
  const [bflowPreview, setBflowPreview] = useState<any>(null);
  const [bflowLoading, setBflowLoading] = useState(false);
  const [bflowAction, setBflowAction] = useState<string>("skip");
  const [bflowImporting, setBflowImporting] = useState(false);
  const [bflowResult, setBflowResult] = useState<any>(null);
  const [bflowEnabled, setBflowEnabled] = useState(false);
  const [perCodeActions, setPerCodeActions] = useState<Record<string, string>>({});
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    const checkBflow = async () => {
      try {
        // F-9 fix: probe bằng endpoint /enabled (luôn 200) thay /preview (403)
        // → hết console error đỏ mỗi tab settings. Guard /preview giữ nguyên.
        const res = await apiClient.get("/tenant-admin/bflow-shifts/enabled", { baseURL: getApiBase("v2") });
        if (res.data?.enabled) setBflowEnabled(true);
      } catch {
        setBflowEnabled(false);
      }
    };
    checkBflow();
  }, []);

  if (loading) return <div style={styles.card}><p style={{ color: "#6b7280" }}>Đang tải Mã Ca...</p></div>;

  const defaultCount = shiftTypes.filter(s => s.is_default).length;
  const bflowCount = shiftTypes.filter(s => s.source === "bflow").length;
  const customCount = shiftTypes.length - defaultCount - bflowCount;

  const openBflowImport = async () => {
    setShowBflowImport(true);
    setBflowLoading(true);
    setBflowResult(null);
    try {
      const res = await apiClient.get("/tenant-admin/bflow-shifts/preview", { baseURL: getApiBase("v2") });
      setBflowPreview(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Không thể tải preview BFlow");
    } finally { setBflowLoading(false); }
  };

  const executeBflowImport = async () => {
    if (!bflowPreview) return;
    setBflowImporting(true);
    setShowConfirm(false);
    try {
      const codes = bflowPreview.items.map((i: any) => i.code);
      const hasPerCode = Object.keys(perCodeActions).length > 0;
      const res = await apiClient.post("/tenant-admin/bflow-shifts/import",
        { codes, duplicate_action: bflowAction, ...(hasPerCode ? { per_code_actions: perCodeActions } : {}) },
        { baseURL: getApiBase("v2") }
      );
      setBflowResult(res.data);
      await fetchShiftTypes();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Import thất bại");
    } finally { setBflowImporting(false); }
  };

  /** FR-006b: Batch action — apply to all duplicate/in_use rows */
  const applyBatchAction = (action: string) => {
    setBflowAction(action);
    if (!bflowPreview) return;
    const updated: Record<string, string> = {};
    bflowPreview.items.forEach((item: any) => {
      if (item.status === "duplicate" || item.status === "in_use") {
        updated[item.code] = action;
      }
    });
    setPerCodeActions(updated);
  };

  /** FR-006d: Confirmation summary */
  const getImportSummary = () => {
    if (!bflowPreview) return { newCount: 0, overwrite: 0, skip: 0, rename: 0 };
    let overwrite = 0, skip = 0, rename = 0;
    const newCount = bflowPreview.items.filter((i: any) => i.status === "new").length;
    bflowPreview.items.forEach((item: any) => {
      if (item.status === "duplicate" || item.status === "in_use") {
        const action = perCodeActions[item.code] || bflowAction;
        if (action === "overwrite") overwrite++;
        else if (action === "rename") rename++;
        else skip++;
      }
    });
    return { newCount, overwrite, skip, rename };
  };

  return (
    <div style={styles.card}>
      {/* Header — DD-0031 §3.1 + DD-0038 BFlow import button */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h3 style={{ margin: 0, fontSize: "16px", color: "#1f2937" }}>Quản lý Mã Ca</h3>
        <div style={{ display: "flex", gap: "8px" }}>
          {bflowEnabled && (
            <button style={{ ...styles.btn("secondary"), background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d" }}
              onClick={openBflowImport}>
              Import từ BFlow
            </button>
          )}
          <button style={styles.btn("primary")} onClick={openCreate}>+ Thêm Mã Ca</button>
        </div>
      </div>

      {/* DD-0038 §2.2: BFlow Import Modal */}
      {showBflowImport && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "white", borderRadius: "12px", padding: "24px", maxWidth: "700px", width: "90%", maxHeight: "80vh", overflow: "auto" }}>
            <h3 style={{ margin: "0 0 16px", fontSize: "18px", color: "#1f2937" }}>Import Mã Ca từ BFlow</h3>

            {bflowLoading && <p style={{ color: "#6b7280" }}>Đang tải preview...</p>}

            {bflowPreview && !bflowResult && !showConfirm && (
              <>
                {/* Summary badges */}
                <div style={{ display: "flex", gap: "12px", marginBottom: "12px" }}>
                  <div style={{ padding: "8px 16px", background: "#dcfce7", borderRadius: "8px", fontSize: "14px" }}>
                    ✅ Mới: <strong>{bflowPreview.summary.new}</strong>
                  </div>
                  <div style={{ padding: "8px 16px", background: "#fef3c7", borderRadius: "8px", fontSize: "14px" }}>
                    ⚠️ Trùng: <strong>{bflowPreview.summary.duplicate}</strong>
                  </div>
                  <div style={{ padding: "8px 16px", background: "#fee2e2", borderRadius: "8px", fontSize: "14px" }}>
                    🔴 Đang dùng: <strong>{bflowPreview.summary.in_use}</strong>
                  </div>
                </div>

                {/* FR-006c: Legend */}
                <div style={{ background: "#f9fafb", borderRadius: "8px", padding: "10px 14px", marginBottom: "12px", fontSize: "12px", color: "#6b7280" }}>
                  <strong>Chú thích:</strong>{" "}
                  <span style={{ color: "#16a34a" }}>● Xanh = Mới (an toàn import)</span>{" · "}
                  <span style={{ color: "#d97706" }}>● Vàng = Trùng code (cần quyết định)</span>{" · "}
                  <span style={{ color: "#dc2626" }}>● Đỏ = Đang dùng trong lịch (cẩn thận)</span>
                </div>

                {/* FR-006b: Batch action header */}
                {(bflowPreview.summary.duplicate > 0 || bflowPreview.summary.in_use > 0) && (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px", padding: "8px 12px", background: "#fffbeb", borderRadius: "8px", border: "1px solid #fcd34d" }}>
                    <span style={{ fontSize: "13px", fontWeight: 600, color: "#92400e" }}>Chọn tất cả mã trùng:</span>
                    <select style={{ padding: "4px 8px", borderRadius: "6px", border: "1px solid #d1d5db", fontSize: "13px" }}
                      value={bflowAction}
                      onChange={(e) => applyBatchAction(e.target.value)}>
                      <option value="skip">Bỏ qua — giữ mã hiện tại</option>
                      <option value="overwrite">Ghi đè — thay bằng BFlow (không hoàn tác)</option>
                      <option value="rename">Đổi tên — import thành code_bflow</option>
                    </select>
                  </div>
                )}

                {/* Preview table with per-row action */}
                <div style={{ maxHeight: "300px", overflow: "auto", marginBottom: "16px" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                      <tr style={{ background: "#f9fafb" }}>
                        <th style={{ padding: "8px", textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>Mã</th>
                        <th style={{ padding: "8px", textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>Tên</th>
                        <th style={{ padding: "8px", textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>Giờ</th>
                        <th style={{ padding: "8px", textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>Trạng thái</th>
                        <th style={{ padding: "8px", textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>Hành động</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bflowPreview.items.map((item: any) => (
                        <tr key={item.code} style={{ borderBottom: "1px solid #f3f4f6", background: item.status === "in_use" ? "#fef2f2" : "transparent" }}>
                          <td style={{ padding: "6px 8px", fontFamily: "monospace" }}>{item.code}</td>
                          <td style={{ padding: "6px 8px" }}>{item.name}</td>
                          <td style={{ padding: "6px 8px" }}>{item.start_time} — {item.end_time}</td>
                          <td style={{ padding: "6px 8px" }}>
                            {item.status === "new" && <span style={{ color: "#16a34a" }}>● Mới</span>}
                            {item.status === "duplicate" && <span style={{ color: "#d97706" }}>● Trùng ({item.existing_source})</span>}
                            {item.status === "in_use" && <span style={{ color: "#dc2626" }}>● Đang dùng ({item.schedule_count} lịch)</span>}
                          </td>
                          <td style={{ padding: "6px 8px" }}>
                            {item.status === "new" ? (
                              <span style={{ color: "#16a34a", fontSize: "12px" }}>Import</span>
                            ) : (
                              <select style={{ padding: "2px 6px", borderRadius: "4px", border: "1px solid #d1d5db", fontSize: "12px" }}
                                value={perCodeActions[item.code] || bflowAction}
                                onChange={(e) => setPerCodeActions(prev => ({ ...prev, [item.code]: e.target.value }))}>
                                <option value="skip">Bỏ qua</option>
                                <option value="overwrite">Ghi đè</option>
                                <option value="rename">Đổi tên</option>
                              </select>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Actions — FR-006d: trigger confirmation */}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                  <button style={styles.btn("secondary")} onClick={() => { setShowBflowImport(false); setBflowPreview(null); setPerCodeActions({}); }}>Hủy</button>
                  <button style={{ ...styles.btn("primary"), background: "#f59e0b", borderColor: "#d97706" }}
                    onClick={() => setShowConfirm(true)} disabled={bflowImporting}>
                    Tiếp tục Import
                  </button>
                </div>
              </>
            )}

            {/* FR-006d: Confirmation dialog */}
            {bflowPreview && showConfirm && !bflowResult && (() => {
              const summary = getImportSummary();
              return (
                <div>
                  <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: "8px", padding: "16px", marginBottom: "16px" }}>
                    <p style={{ margin: "0 0 8px", fontSize: "15px", fontWeight: 600, color: "#92400e" }}>Xác nhận Import</p>
                    <ul style={{ margin: 0, paddingLeft: "20px", fontSize: "14px", color: "#1f2937", lineHeight: "1.8" }}>
                      <li><strong>{summary.newCount}</strong> mã ca mới sẽ được import</li>
                      {summary.overwrite > 0 && <li style={{ color: "#dc2626" }}><strong>{summary.overwrite}</strong> mã ca sẽ bị <strong>ghi đè</strong> (dữ liệu cũ mất, không hoàn tác)</li>}
                      {summary.skip > 0 && <li><strong>{summary.skip}</strong> mã ca sẽ được <strong>bỏ qua</strong></li>}
                      {summary.rename > 0 && <li><strong>{summary.rename}</strong> mã ca sẽ được <strong>đổi tên</strong> thành code_bflow</li>}
                    </ul>
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                    <button style={styles.btn("secondary")} onClick={() => setShowConfirm(false)}>Quay lại</button>
                    <button style={{ ...styles.btn("primary"), background: "#dc2626", borderColor: "#b91c1c" }}
                      onClick={executeBflowImport} disabled={bflowImporting}>
                      {bflowImporting ? "Đang import..." : "Xác nhận Import"}
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Result summary */}
            {bflowResult && (
              <div>
                <div style={{ background: "#dcfce7", borderRadius: "8px", padding: "16px", marginBottom: "16px" }}>
                  <p style={{ margin: 0, fontSize: "14px", color: "#166534" }}>
                    ✅ Import hoàn tất: {bflowResult.imported} mới, {bflowResult.overwritten} ghi đè,
                    {" "}{bflowResult.skipped} bỏ qua, {bflowResult.renamed} đổi tên
                  </p>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button style={styles.btn("primary")} onClick={() => { setShowBflowImport(false); setBflowPreview(null); setBflowResult(null); }}>Đóng</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", padding: "12px", marginBottom: "16px", color: "#dc2626", fontSize: "14px" }}>
          {error}
        </div>
      )}

      {/* Create/Edit form — DD-0031 §3.2 */}
      {showForm && (
        <div ref={formRef} style={{ background: "#f9fafb", borderRadius: "8px", padding: "16px", marginBottom: "20px", border: "1px solid #e5e7eb" }}>
          <h4 style={{ margin: "0 0 12px", fontSize: "14px", color: "#374151" }}>{editId ? "Sửa Mã Ca" : "Thêm Mã Ca mới"}</h4>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
            <div>
              <label style={styles.label}>Mã Ca *</label>
              <input style={styles.input} value={formData.code} disabled={!!editId}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "") })}
                placeholder="CA_TRUA" maxLength={20} />
            </div>
            <div>
              <label style={styles.label}>Tên *</label>
              <input style={styles.input} value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ca trưa" maxLength={100} />
            </div>
            <div>
              <label style={styles.label}>Loại ca *</label>
              <select style={styles.input} value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}>
                {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label style={styles.label}>Màu sắc</label>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input type="color" value={formData.color_code}
                  onChange={(e) => setFormData({ ...formData, color_code: e.target.value })}
                  style={{ width: "36px", height: "36px", border: "1px solid #d1d5db", borderRadius: "4px", cursor: "pointer" }} />
                <input style={{ ...styles.input, flex: 1 }} value={formData.color_code}
                  onChange={(e) => setFormData({ ...formData, color_code: e.target.value })}
                  placeholder="#3B82F6" maxLength={7} />
              </div>
            </div>
            <div>
              <label style={styles.label}>Giờ bắt đầu *</label>
              <TimeTextInput style={styles.input} value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })} />
            </div>
            <div>
              <label style={styles.label}>Giờ kết thúc *</label>
              <TimeTextInput style={styles.input} value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })} />
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
            <input type="checkbox" checked={formData.is_overnight}
              onChange={(e) => setFormData({ ...formData, is_overnight: e.target.checked })} />
            <span style={{ fontSize: "14px" }}>Ca qua đêm (cross-midnight)</span>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button style={styles.btn("primary")} onClick={handleSave} disabled={saving || !formData.code || !formData.name}>
              {saving ? "Đang lưu..." : (editId ? "Cập nhật" : "Tạo Mã Ca")}
            </button>
            <button style={styles.btn("secondary")} onClick={() => { setShowForm(false); setEditId(null); }}>Hủy</button>
          </div>
        </div>
      )}

      {/* Table — DD-0031 §3.1 */}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
        <thead>
          <tr style={{ borderBottom: "2px solid #e5e7eb", textAlign: "left" }}>
            <th style={{ padding: "8px", width: "32px" }}></th>
            <th style={{ padding: "8px" }}>Mã</th>
            <th style={{ padding: "8px" }}>Tên</th>
            <th style={{ padding: "8px" }}>Giờ</th>
            <th style={{ padding: "8px" }}>Loại</th>
            <th style={{ padding: "8px" }}>Nguồn</th>
            <th style={{ padding: "8px" }}></th>
          </tr>
        </thead>
        <tbody>
          {shiftTypes.map((st) => {
            const sourceBadge = SOURCE_BADGES[st.source] || SOURCE_BADGES.manual;
            const isBflow = st.source === "bflow";
            return (
              <tr key={st.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                {/* Color dot — DD-0033 §2.4 */}
                <td style={{ padding: "8px" }}>
                  <span style={{ display: "inline-block", width: "12px", height: "12px", borderRadius: "50%", background: st.color_code || "#10B981" }} />
                </td>
                <td style={{ padding: "8px", fontFamily: "monospace", fontWeight: 600 }}>{st.code}</td>
                <td style={{ padding: "8px" }}>
                  {st.name}
                  {st.is_default && (
                    <span style={{ marginLeft: "8px", fontSize: "11px", background: "#dbeafe", color: "#1d4ed8", padding: "2px 6px", borderRadius: "4px" }}>
                      Template
                    </span>
                  )}
                </td>
                <td style={{ padding: "8px", color: "#6b7280" }}>{st.start_time} — {st.end_time}</td>
                <td style={{ padding: "8px" }}>{CATEGORY_LABELS[st.category] || st.category}</td>
                <td style={{ padding: "8px" }}>
                  <span style={{ fontSize: "11px", background: sourceBadge.bg, color: sourceBadge.color, padding: "2px 6px", borderRadius: "4px" }}>
                    {sourceBadge.label}
                  </span>
                </td>
                <td style={{ padding: "8px" }}>
                  {isBflow ? (
                    <span style={{ color: "#9ca3af", fontSize: "13px" }} title="BFlow — chỉ đọc">🔒</span>
                  ) : (
                    <span style={{ display: "flex", gap: "8px" }}>
                      <button onClick={() => openEdit(st)} style={{ background: "none", border: "none", color: "#3b82f6", cursor: "pointer", fontSize: "13px" }}>Sửa</button>
                      <button onClick={() => handleDelete(st)} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: "13px" }}>Xóa</button>
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {shiftTypes.length === 0 && (
        <p style={{ textAlign: "center", color: "#9ca3af", padding: "20px" }}>Chưa có Mã Ca nào</p>
      )}

      {/* Summary — DD-0031 §3.1 */}
      {shiftTypes.length > 0 && (
        <p style={{ textAlign: "right", fontSize: "12px", color: "#9ca3af", marginTop: "12px" }}>
          Tổng: {shiftTypes.length} mã ca ({defaultCount} template{bflowCount > 0 ? `, ${bflowCount} BFlow` : ""}{customCount > 0 ? `, ${customCount} tùy chỉnh` : ""})
        </p>
      )}
    </div>
  );
};

export default TenantSettings;
